import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { accountsAPI } from '../lib/api';
import { ForecastCard } from '../components/ForecastCard';
import InlineIntegrityChip from '../components/InlineIntegrityChip';
import PnLTruthBanner from '../components/PnLTruthBanner';
import TruthSparkline from '../components/TruthSparkline';
import PulseTruthSurface from '../components/PulseTruthSurface';
import IntegrationStatusPanel from '../components/IntegrationStatusPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  CalendarIcon, TrendingUp, TrendingDown, AlertTriangle, 
  RefreshCw, Loader2, DollarSign, Package,
  Utensils, Wine, Coffee, Flame, Box, CheckCircle2, XCircle,
  FileText, ShoppingCart, Zap, Send, History, Lock, Eye,
  GitCompare, ArrowUp, ArrowDown, Minus, Info, Smartphone, MessageSquare, Copy, Upload,
  Target, Pencil, Save, X, Wallet, Users,
} from 'lucide-react';
import { format } from 'date-fns';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const formatCurrency = (value) => {
  // Em-dash for null/undefined — callers must pass real 0 to get "₹0".
  // This is the anchor of the UI truth contract: the page must never
  // silently convert "unknown" into "0".
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

// Quick Actions Panel Component
const QuickActionsPanel = ({ dateFrom, dateTo, onSyncComplete }) => {
  const [syncingPOS, setSyncingPOS] = useState(false);
  const [syncingBills, setSyncingBills] = useState(false);
  const [lastPOSSync, setLastPOSSync] = useState(null);
  const [lastBillsSync, setLastBillsSync] = useState(null);

  const handleSyncPOS = async () => {
    setSyncingPOS(true);
    try {
      const response = await accountsAPI.syncPOSSales({
        date_from: dateFrom,
        date_to: dateTo
      });
      const data = response.data;
      
      setLastPOSSync({
        timestamp: new Date().toISOString(),
        processed: data.processed_order_ids?.length || data.synced || 0,
        failed: data.failed_order_ids?.length || 0,
        skipped: data.locked_period_skipped?.length || 0,
        status: data.status
      });

      if (data.status === 'OK' || data.synced > 0 || data.processed_order_ids?.length > 0) {
        toast.success(`POS Sync Complete`, {
          description: `${data.processed_order_ids?.length || data.synced || 0} orders synced`
        });
        onSyncComplete?.();
      } else if (data.status === 'NO_DATA') {
        toast.info('POS Sync', { description: 'No new orders to sync' });
      } else {
        toast.warning('POS Sync', { description: data.message || 'Sync completed with issues' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      toast.error('POS Sync Failed', {
        description: err.response?.data?.detail || 'Could not sync POS sales'
      });
      setLastPOSSync({
        timestamp: new Date().toISOString(),
        error: err.response?.data?.detail || 'Sync failed',
        status: 'ERROR'
      });
    } finally {
      setSyncingPOS(false);
    }
  };

  const handleSyncBills = async () => {
    setSyncingBills(true);
    try {
      const response = await accountsAPI.syncVendorBills();
      const data = response.data;
      
      setLastBillsSync({
        timestamp: new Date().toISOString(),
        synced: data.synced || 0,
        updated: data.updated || 0,
        skipped: data.locked_period_skipped?.length || 0,
        taxEntries: data.tax_entries_created || 0,
        status: 'OK'
      });

      toast.success(`Bills Sync Complete`, {
        description: `${data.synced} new, ${data.updated} updated${data.locked_period_skipped?.length ? `, ${data.locked_period_skipped.length} skipped (locked)` : ''}`
      });
      onSyncComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      toast.error('Bills Sync Failed', {
        description: err.response?.data?.detail || 'Could not sync vendor bills'
      });
      setLastBillsSync({
        timestamp: new Date().toISOString(),
        error: err.response?.data?.detail || 'Sync failed',
        status: 'ERROR'
      });
    } finally {
      setSyncingBills(false);
    }
  };

  return (
    <Card className="bg-card/30 backdrop-blur-sm border-border/30" data-testid="quick-actions-panel">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium">Quick Actions</span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sync POS Button */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncPOS}
                disabled={syncingPOS}
                className="h-8"
                data-testid="sync-pos-btn"
              >
                {syncingPOS ? (
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <ShoppingCart className="h-3 w-3 mr-1.5" />
                )}
                Sync POS
              </Button>
              {lastPOSSync && (
                <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground">
                  {lastPOSSync.status === 'ERROR' ? (
                    <XCircle className="w-3 h-3 text-rose-400" />
                  ) : (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  )}
                  <span>{lastPOSSync.processed || 0} synced</span>
                  {lastPOSSync.skipped > 0 && (
                    <span className="text-amber-400">({lastPOSSync.skipped} locked)</span>
                  )}
                </div>
              )}
            </div>

            {/* Sync Bills Button */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncBills}
                disabled={syncingBills}
                className="h-8"
                data-testid="sync-bills-btn"
              >
                {syncingBills ? (
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                ) : (
                  <FileText className="h-3 w-3 mr-1.5" />
                )}
                Sync Bills
              </Button>
              {lastBillsSync && (
                <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground">
                  {lastBillsSync.status === 'ERROR' ? (
                    <XCircle className="w-3 h-3 text-rose-400" />
                  ) : (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  )}
                  <span>{lastBillsSync.synced}+{lastBillsSync.updated}</span>
                  {lastBillsSync.skipped > 0 && (
                    <span className="text-amber-400">({lastBillsSync.skipped} locked)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Publish P&L Dialog
const PublishPnLDialog = ({ dateFrom, dateTo, pnlData, onPublished }) => {
  const [open, setOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [periodType, setPeriodType] = useState('CUSTOM');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await accountsAPI.publishPnL({
        date_from: dateFrom,
        date_to: dateTo,
        period_type: periodType,
        label: label || undefined,
        notes: notes || undefined
      });
      
      toast.success('P&L Published', {
        description: `${response.data.label} - Period locked successfully`
      });
      setOpen(false);
      setLabel('');
      setNotes('');
      onPublished?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      toast.error('Publish Failed', {
        description: err.response?.data?.detail || 'Could not publish P&L'
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="default" 
          size="sm" 
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="publish-pnl-btn"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Publish
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Publish P&L Snapshot
          </DialogTitle>
          <DialogDescription>
            Create an immutable P&L snapshot. This will lock the period to prevent retroactive changes.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Date Range</Label>
            <div className="text-sm font-medium bg-muted/50 p-2 rounded">
              {dateFrom} to {dateTo}
            </div>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="periodType">Period Type</Label>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WEEK">Week</SelectItem>
                <SelectItem value="MONTH">Month</SelectItem>
                <SelectItem value="QUARTER">Quarter</SelectItem>
                <SelectItem value="CUSTOM">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="label">Label (optional)</Label>
            <Input
              id="label"
              placeholder="e.g., February 2026"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any comments for this snapshot..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          
          {pnlData && (
            <div className="bg-muted/30 p-3 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Net Sales:</span>
                <span className="font-mono font-medium">{formatCurrency(pnlData.sales?.net_sales)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gross Profit:</span>
                <span className="font-mono font-medium text-emerald-400">{formatCurrency(pnlData.profit?.gross_profit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gross Margin:</span>
                <span className="font-mono font-medium">{formatPercent(pnlData.profit?.gross_margin_pct)}</span>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePublish} 
            disabled={publishing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {publishing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Lock className="h-4 w-4 mr-2" />
            )}
            Publish & Lock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Compare Periods Dialog
const ComparePeriodsDialog = ({ snapshots, open, onOpenChange }) => {
  const [snapshotA, setSnapshotA] = useState(null);
  const [snapshotB, setSnapshotB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [fullSnapshotA, setFullSnapshotA] = useState(null);
  const [fullSnapshotB, setFullSnapshotB] = useState(null);
  const [viewMode, setViewMode] = useState('absolute'); // 'absolute' | 'percentage'

  // Auto-select defaults when dialog opens
  useEffect(() => {
    if (open && snapshots.length >= 2) {
      setSnapshotA(snapshots[0]?.id);
      setSnapshotB(snapshots[1]?.id);
    }
  }, [open, snapshots]);

  // Fetch full snapshot data when selection changes
  useEffect(() => {
    const fetchFullSnapshot = async (id, setLoading, setData) => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await accountsAPI.getPublishedPnLDetail(id);
        setData(response.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Operation failed");
        toast.error('Failed to load snapshot');
      } finally {
        setLoading(false);
      }
    };

    if (snapshotA) fetchFullSnapshot(snapshotA, setLoadingA, setFullSnapshotA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotA]);

  useEffect(() => {
    const fetchFullSnapshot = async (id, setLoading, setData) => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await accountsAPI.getPublishedPnLDetail(id);
        setData(response.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Operation failed");
      } finally {
        setLoading(false);
      }
    };

    if (snapshotB) fetchFullSnapshot(snapshotB, setLoadingB, setFullSnapshotB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotB]);

  // Calculate delta and delta percentage
  const calcDelta = (a, b) => {
    const valA = a || 0;
    const valB = b || 0;
    const delta = valA - valB;
    const deltaPct = valB !== 0 ? ((valA - valB) / Math.abs(valB)) * 100 : (valA !== 0 ? 100 : 0);
    return { delta, deltaPct };
  };

  // Get delta indicator icon and color
  const getDeltaIndicator = (delta, invertColors = false) => {
    if (Math.abs(delta) < 0.01) return { icon: Minus, color: 'text-muted-foreground' };
    const isPositive = delta > 0;
    if (invertColors) {
      // For costs: positive delta is bad (red), negative is good (green)
      return isPositive 
        ? { icon: ArrowUp, color: 'text-rose-400' }
        : { icon: ArrowDown, color: 'text-emerald-400' };
    }
    // For revenue/profit: positive delta is good (green), negative is bad (red)
    return isPositive 
      ? { icon: ArrowUp, color: 'text-emerald-400' }
      : { icon: ArrowDown, color: 'text-rose-400' };
  };

  // Comparison row component
  const ComparisonRow = ({ label, valueA, valueB, isCurrency = true, invertColors = false, indent = false }) => {
    const { delta, deltaPct } = calcDelta(valueA, valueB);
    const { icon: DeltaIcon, color } = getDeltaIndicator(delta, invertColors);
    
    return (
      <div className={`grid grid-cols-5 gap-2 py-2 text-sm border-b border-border/20 ${indent ? 'pl-4' : ''}`}>
        <div className="text-muted-foreground">{label}</div>
        <div className="font-mono text-right">{isCurrency ? formatCurrency(valueA) : formatPercent(valueA)}</div>
        <div className="font-mono text-right">{isCurrency ? formatCurrency(valueB) : formatPercent(valueB)}</div>
        <div className={`font-mono text-right flex items-center justify-end gap-1 ${color}`}>
          <DeltaIcon className="h-3 w-3" />
          {isCurrency ? formatCurrency(Math.abs(delta)) : formatPercent(Math.abs(delta))}
        </div>
        <div className={`font-mono text-right ${color}`}>
          {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%
        </div>
      </div>
    );
  };

  // Section header
  const SectionHeader = ({ title }) => (
    <div className="grid grid-cols-5 gap-2 py-2 text-xs font-semibold uppercase tracking-wider bg-muted/30 px-2 rounded mt-4">
      <div>{title}</div>
      <div className="text-right">Snapshot A</div>
      <div className="text-right">Snapshot B</div>
      <div className="text-right">Δ Value</div>
      <div className="text-right">Δ %</div>
    </div>
  );

  // Calculate top drivers (biggest movers)
  const calculateDrivers = () => {
    if (!fullSnapshotA || !fullSnapshotB) return [];

    const drivers = [];
    const netSalesA = fullSnapshotA.sales?.net_sales || 0;
    const netSalesB = fullSnapshotB.sales?.net_sales || 0;

    // Helper to add driver
    const addDriver = (label, valueA, valueB, baseA, baseB, isCost = false) => {
      const delta = valueA - valueB;
      const ptsA = baseA > 0 ? (valueA / baseA) * 100 : 0;
      const ptsB = baseB > 0 ? (valueB / baseB) * 100 : 0;
      const ptsDelta = ptsA - ptsB;
      
      drivers.push({
        label,
        delta,
        deltaPct: valueB !== 0 ? ((valueA - valueB) / Math.abs(valueB)) * 100 : 0,
        ptsDelta,
        isCost,
        impact: Math.abs(delta)
      });
    };

    // Sales drivers
    addDriver('Net Sales', netSalesA, netSalesB, 1, 1, false);
    addDriver('Food Sales', fullSnapshotA.sales?.food_sales || 0, fullSnapshotB.sales?.food_sales || 0, 1, 1, false);
    addDriver('Alcohol Sales', fullSnapshotA.sales?.alcohol_sales || 0, fullSnapshotB.sales?.alcohol_sales || 0, 1, 1, false);

    // COGS drivers
    addDriver('Food COGS', fullSnapshotA.cogs?.food?.total || 0, fullSnapshotB.cogs?.food?.total || 0, 
      fullSnapshotA.sales?.food_sales || 1, fullSnapshotB.sales?.food_sales || 1, true);
    addDriver('Liquor COGS', fullSnapshotA.cogs?.liquor?.total || 0, fullSnapshotB.cogs?.liquor?.total || 0,
      fullSnapshotA.sales?.alcohol_sales || 1, fullSnapshotB.sales?.alcohol_sales || 1, true);
    addDriver('Beverage COGS', fullSnapshotA.cogs?.beverage?.total || 0, fullSnapshotB.cogs?.beverage?.total || 0,
      fullSnapshotA.sales?.beverage_sales || 1, fullSnapshotB.sales?.beverage_sales || 1, true);

    // Opex drivers
    addDriver('Utility', fullSnapshotA.opex?.utility?.total || 0, fullSnapshotB.opex?.utility?.total || 0, netSalesA, netSalesB, true);
    addDriver('Packing', fullSnapshotA.opex?.packing?.total || 0, fullSnapshotB.opex?.packing?.total || 0, netSalesA, netSalesB, true);

    // Profit drivers
    addDriver('Gross Profit', fullSnapshotA.profit?.gross_profit || 0, fullSnapshotB.profit?.gross_profit || 0, 1, 1, false);
    addDriver('Net Profit', fullSnapshotA.profit?.net_profit || 0, fullSnapshotB.profit?.net_profit || 0, 1, 1, false);

    // Sort by absolute impact and return top 5
    return drivers.sort((a, b) => b.impact - a.impact).slice(0, 5);
  };

  const topDrivers = calculateDrivers();
  const loading = loadingA || loadingB;
  const hasData = fullSnapshotA && fullSnapshotB;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Compare P&L Periods
          </DialogTitle>
          <DialogDescription>
            Side-by-side comparison of published P&L snapshots to identify trends and drivers.
          </DialogDescription>
        </DialogHeader>

        {/* Snapshot Selectors */}
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <Label>Snapshot A (Current)</Label>
            <Select value={snapshotA || ''} onValueChange={setSnapshotA}>
              <SelectTrigger>
                <SelectValue placeholder="Select snapshot" />
              </SelectTrigger>
              <SelectContent>
                {snapshots.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label} ({s.date_from})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fullSnapshotA && (
              <p className="text-xs text-muted-foreground">
                {fullSnapshotA.date_from} → {fullSnapshotA.date_to}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Snapshot B (Previous)</Label>
            <Select value={snapshotB || ''} onValueChange={setSnapshotB}>
              <SelectTrigger>
                <SelectValue placeholder="Select snapshot" />
              </SelectTrigger>
              <SelectContent>
                {snapshots.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label} ({s.date_from})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fullSnapshotB && (
              <p className="text-xs text-muted-foreground">
                {fullSnapshotB.date_from} → {fullSnapshotB.date_to}
              </p>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading comparison data...</span>
          </div>
        )}

        {hasData && !loading && (
          <>
            {/* Top Drivers Summary */}
            <Card className="bg-muted/20 border-border/30 mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Top 5 Drivers
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {topDrivers.map((driver, idx) => {
                    const isPositive = driver.isCost ? driver.delta < 0 : driver.delta > 0;
                    const color = isPositive ? 'text-emerald-400' : 'text-rose-400';
                    const Icon = driver.delta > 0 ? ArrowUp : driver.delta < 0 ? ArrowDown : Minus;
                    
                    return (
                      <div key={`driver-${driver.label || idx}`} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{driver.label}</span>
                        <span className={`font-mono flex items-center gap-1 ${color}`}>
                          <Icon className="h-3 w-3" />
                          {formatCurrency(Math.abs(driver.delta))}
                          <span className="text-xs">
                            ({driver.deltaPct >= 0 ? '+' : ''}{driver.deltaPct.toFixed(1)}%)
                          </span>
                          {Math.abs(driver.ptsDelta) > 0.1 && (
                            <span className="text-xs text-muted-foreground">
                              [{driver.ptsDelta >= 0 ? '+' : ''}{driver.ptsDelta.toFixed(1)} pts]
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <div className="space-y-1">
              {/* Sales Section */}
              <SectionHeader title="Sales" />
              <ComparisonRow 
                label="Gross Sales" 
                valueA={fullSnapshotA.sales?.gross_sales} 
                valueB={fullSnapshotB.sales?.gross_sales} 
              />
              <ComparisonRow 
                label="GST" 
                valueA={fullSnapshotA.sales?.gst_amount} 
                valueB={fullSnapshotB.sales?.gst_amount}
                invertColors={true}
              />
              <ComparisonRow 
                label="VAT" 
                valueA={fullSnapshotA.sales?.vat_amount} 
                valueB={fullSnapshotB.sales?.vat_amount}
                invertColors={true}
              />
              <ComparisonRow 
                label="Net Sales" 
                valueA={fullSnapshotA.sales?.net_sales} 
                valueB={fullSnapshotB.sales?.net_sales} 
              />
              <ComparisonRow 
                label="Food Sales" 
                valueA={fullSnapshotA.sales?.food_sales} 
                valueB={fullSnapshotB.sales?.food_sales}
                indent={true}
              />
              <ComparisonRow 
                label="Alcohol Sales" 
                valueA={fullSnapshotA.sales?.alcohol_sales} 
                valueB={fullSnapshotB.sales?.alcohol_sales}
                indent={true}
              />

              {/* COGS Section */}
              <SectionHeader title="Cost of Goods Sold" />
              <ComparisonRow 
                label="Food COGS" 
                valueA={fullSnapshotA.cogs?.food?.total} 
                valueB={fullSnapshotB.cogs?.food?.total}
                invertColors={true}
              />
              <ComparisonRow 
                label="Food Cost %" 
                valueA={fullSnapshotA.cogs?.food?.pct_of_sales} 
                valueB={fullSnapshotB.cogs?.food?.pct_of_sales}
                isCurrency={false}
                invertColors={true}
                indent={true}
              />
              <ComparisonRow 
                label="Liquor COGS" 
                valueA={fullSnapshotA.cogs?.liquor?.total} 
                valueB={fullSnapshotB.cogs?.liquor?.total}
                invertColors={true}
              />
              <ComparisonRow 
                label="Liquor Cost %" 
                valueA={fullSnapshotA.cogs?.liquor?.pct_of_sales} 
                valueB={fullSnapshotB.cogs?.liquor?.pct_of_sales}
                isCurrency={false}
                invertColors={true}
                indent={true}
              />
              <ComparisonRow 
                label="Beverage COGS" 
                valueA={fullSnapshotA.cogs?.beverage?.total} 
                valueB={fullSnapshotB.cogs?.beverage?.total}
                invertColors={true}
              />
              <ComparisonRow 
                label="Total COGS" 
                valueA={fullSnapshotA.cogs?.total_cogs} 
                valueB={fullSnapshotB.cogs?.total_cogs}
                invertColors={true}
              />

              {/* Profitability Section */}
              <SectionHeader title="Profitability" />
              <ComparisonRow 
                label="Gross Profit" 
                valueA={fullSnapshotA.profit?.gross_profit} 
                valueB={fullSnapshotB.profit?.gross_profit} 
              />
              <ComparisonRow 
                label="Gross Margin %" 
                valueA={fullSnapshotA.profit?.gross_margin_pct} 
                valueB={fullSnapshotB.profit?.gross_margin_pct}
                isCurrency={false}
              />

              {/* Opex Section */}
              <SectionHeader title="Operating Expenses" />
              <ComparisonRow 
                label="Utility" 
                valueA={fullSnapshotA.opex?.utility?.total} 
                valueB={fullSnapshotB.opex?.utility?.total}
                invertColors={true}
              />
              <ComparisonRow 
                label="Packing" 
                valueA={fullSnapshotA.opex?.packing?.total} 
                valueB={fullSnapshotB.opex?.packing?.total}
                invertColors={true}
              />
              <ComparisonRow 
                label="Total Opex" 
                valueA={fullSnapshotA.opex?.total_opex} 
                valueB={fullSnapshotB.opex?.total_opex}
                invertColors={true}
              />

              {/* Net Section */}
              <SectionHeader title="Net Results" />
              <ComparisonRow 
                label="Net Profit" 
                valueA={fullSnapshotA.profit?.net_profit} 
                valueB={fullSnapshotB.profit?.net_profit} 
              />
              <ComparisonRow 
                label="Net Margin %" 
                valueA={fullSnapshotA.profit?.net_margin_pct} 
                valueB={fullSnapshotB.profit?.net_margin_pct}
                isCurrency={false}
              />
            </div>

            {/* Confidence Comparison */}
            <div className="mt-4 pt-4 border-t border-border/30">
              <p className="text-xs text-muted-foreground">
                A: {fullSnapshotA.confidence?.pos_orders_count || 0} orders, {fullSnapshotA.confidence?.bills_count || 0} bills | 
                B: {fullSnapshotB.confidence?.pos_orders_count || 0} orders, {fullSnapshotB.confidence?.bills_count || 0} bills
              </p>
            </div>
          </>
        )}

        {!hasData && !loading && snapshots.length < 2 && (
          <div className="text-center py-8 text-muted-foreground">
            <GitCompare className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Need at least 2 published snapshots to compare.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Published Snapshots Panel
const PublishedSnapshotsPanel = ({ onViewSnapshot }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [compareOpen, setCompareOpen] = useState(false);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'all' ? { period_type: activeTab.toUpperCase() } : {};
      const response = await accountsAPI.getPublishedPnLList(params);
      setSnapshots(response.data.snapshots || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  if (loading && snapshots.length === 0) {
    return (
      <Card className="bg-card/30 border-border/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading published snapshots...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card/30 border-border/30" data-testid="published-snapshots-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4" />
              Published Snapshots
            </CardTitle>
            {snapshots.length >= 2 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCompareOpen(true)}
                className="h-7"
                data-testid="compare-periods-btn"
              >
                <GitCompare className="h-3.5 w-3.5 mr-1.5" />
                Compare
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 h-8">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="week" className="text-xs">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-xs">Month</TabsTrigger>
              <TabsTrigger value="quarter" className="text-xs">Quarter</TabsTrigger>
              <TabsTrigger value="custom" className="text-xs">Custom</TabsTrigger>
            </TabsList>
            
            <TabsContent value={activeTab} className="mt-3">
              {snapshots.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">
                  No published snapshots yet
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {snapshots.map((snapshot) => (
                    <div 
                      key={snapshot.id}
                      className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => onViewSnapshot?.(snapshot)}
                    >
                      <div className="flex items-center gap-3">
                        <Lock className="h-3.5 w-3.5 text-emerald-400" />
                        <div>
                          <p className="text-sm font-medium">{snapshot.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {snapshot.date_from} → {snapshot.date_to}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-mono">{formatCurrency(snapshot.sales?.net_sales)}</p>
                          <p className="text-xs text-emerald-400">{formatPercent(snapshot.profit?.gross_margin_pct)}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Compare Periods Dialog */}
      <ComparePeriodsDialog 
        snapshots={snapshots}
        open={compareOpen}
        onOpenChange={setCompareOpen}
      />
    </>
  );
};

// Today's Story Panel (Driver-powered narrative) - GM-First, Actionable
const TodaysStoryPanel = ({ dateFrom, dateTo, onRefresh }) => {
  const [drivers, setDrivers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchDrivers = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const response = await accountsAPI.getPnLDrivers({
        date_from: dateFrom,
        date_to: dateTo
      });
      setDrivers(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  // Refresh when parent triggers
  useEffect(() => {
    if (onRefresh) {
      fetchDrivers();
    }
  }, [onRefresh, fetchDrivers]);

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" data-testid="todays-story-panel-loading">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
            <span className="text-sm text-slate-300">Analyzing drivers...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Truth contract suppression — backend has already gated this endpoint.
  // When state is INVALID/NOT_ACTIONABLE, the bullets list contains a plain
  // suppression headline string. Render a clean card instead of trying to
  // interpret drivers/bullets as decision-grade.
  const storyTruth = drivers?.truth_contract?.state || drivers?.story?.truth_state;
  if (drivers && storyTruth && storyTruth !== 'VALID') {
    const isInvalid = storyTruth === 'INVALID';
    const headline =
      (typeof drivers.story?.bullets?.[0] === 'string' ? drivers.story.bullets[0] : null) ||
      drivers.story?.confidence_note ||
      (isInvalid
        ? "Cost insights unavailable — inventory data not decision-grade"
        : "Directional only — cost insights suppressed");
    return (
      <Card
        className={`bg-gradient-to-br from-slate-900 to-slate-800 ${
          isInvalid ? 'border-rose-500/40' : 'border-amber-500/40'
        }`}
        data-testid={`todays-story-panel-suppressed-${storyTruth.toLowerCase()}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className={`h-4 w-4 mt-0.5 ${isInvalid ? 'text-rose-400' : 'text-amber-400'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isInvalid ? 'text-rose-300' : 'text-amber-300'}`}>
                Today's Story suppressed
              </p>
              <p className="text-xs text-slate-400 mt-1">{headline}</p>
              {drivers.truth_contract?.reason && (
                <p className="text-[10px] text-slate-500 mt-1.5 font-mono">
                  reason: {drivers.truth_contract.reason}
                </p>
              )}
              <p className="text-[10px] text-slate-500 mt-2">
                Resolve inventory/COGS issues above to unlock driver insights.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!drivers || drivers.driver_count === 0) {
    return (
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" data-testid="todays-story-panel-empty">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium text-slate-200">Today's Story</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            No significant changes detected vs previous period. Operations steady.
          </p>
          {drivers?.mode && (
            <p className="text-[10px] text-slate-500 mt-2 font-mono">
              {drivers.story?.confidence_note || `Inventory: ${drivers.mode.inventory}`}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return AlertTriangle;
      case 'warning': return AlertTriangle;
      default: return TrendingUp;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      case 'warning': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      default: return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
    }
  };

  const getDriverTypeIcon = (type) => {
    if (type.includes('SALES')) return TrendingUp;
    if (type.includes('PURCHASE')) return ShoppingCart;
    if (type.includes('COGS')) return Package;
    if (type.includes('PAYROLL')) return DollarSign;
    return Zap;
  };

  // Parse markdown-like bold text
  const renderBulletText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`el-${i}-${part?.slice(0,10)}`} className="text-slate-100">{part.slice(2, -2)}</strong>;
      }
      return <span key={`el-${i}-${part?.slice(0,10)}`}>{part}</span>;
    });
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" data-testid="todays-story-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-200">
            <Zap className="h-4 w-4 text-violet-400" />
            Today's Story
          </CardTitle>
          <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400">
            vs {drivers.compare_period?.date_from || 'previous'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* Story Bullets - GM-First Narrative */}
        {drivers.story?.bullets?.length > 0 && (
          <div className="space-y-3">
            {drivers.story.bullets.map((bullet, idx) => {
              const Icon = getSeverityIcon(bullet.severity);
              const colorClass = getSeverityColor(bullet.severity);
              return (
                <div 
                  key={`bullet-${bullet.text?.slice(0,20) || idx}`} 
                  className={`p-2.5 rounded-lg border ${colorClass}`}
                  data-testid={`story-bullet-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs leading-relaxed text-slate-300">
                        {renderBulletText(bullet.text)}
                      </p>
                      {/* Quick Links */}
                      {bullet.links && bullet.links.length > 0 && (
                        <div className="flex gap-2 mt-1.5">
                          {bullet.links.map((link, linkIdx) => (
                            <a
                              key={link.href || link.label || `link-${linkIdx}`}
                              href={link.href}
                              className="text-[10px] text-violet-400 hover:text-violet-300 underline-offset-2 hover:underline"
                            >
                              {link.label} →
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Confidence Note */}
        {drivers.story?.confidence_note && (
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono bg-slate-800/50 px-2 py-1 rounded">
            <Box className="h-3 w-3" />
            {drivers.story.confidence_note}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-700">
          <div className="text-center">
            <p className="text-[10px] text-slate-500">Sales Δ</p>
            <p className={`text-xs font-mono font-medium ${drivers.summary?.net_sales_change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {drivers.summary?.net_sales_change >= 0 ? '+' : ''}{formatCurrency(drivers.summary?.net_sales_change || 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-slate-500">COGS Δ pts</p>
            <p className={`text-xs font-mono font-medium ${(drivers.summary?.cogs_change_pts || 0) <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {(drivers.summary?.cogs_change_pts || 0) >= 0 ? '+' : ''}{(drivers.summary?.cogs_change_pts || 0).toFixed(1)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-slate-500">Spikes</p>
            <p className="text-xs font-mono font-medium text-slate-300">
              {(drivers.summary?.vendors_with_spikes || 0) + (drivers.summary?.categories_with_spikes || 0)}
            </p>
          </div>
        </div>

        {/* Expandable Drivers List */}
        {drivers.drivers?.length > 0 && (
          <div className="pt-2 border-t border-slate-700">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors"
              data-testid="expand-drivers-btn"
            >
              {expanded ? 'Hide' : 'Show'} {drivers.driver_count} drivers
              {expanded ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            </button>
            
            {expanded && (
              <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {drivers.drivers.slice(0, 10).map((driver, idx) => {
                  const DriverIcon = getDriverTypeIcon(driver.type);
                  return (
                    <div 
                      key={`driver-${driver.label || idx}`} 
                      className="p-2.5 rounded-lg border border-slate-700 bg-slate-800/50 text-xs"
                      data-testid={`driver-item-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <DriverIcon className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-medium text-slate-200">{driver.title}</span>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={`text-[9px] ${
                            driver.severity === 'WARNING' ? 'border-amber-500/50 text-amber-400' :
                            driver.severity === 'CRITICAL' ? 'border-rose-500/50 text-rose-400' :
                            'border-slate-600 text-slate-400'
                          }`}
                        >
                          {driver.type.split('_')[0]}
                        </Badge>
                      </div>
                      
                      {/* Breakdown */}
                      {driver.breakdown && driver.breakdown.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {driver.breakdown.slice(0, 3).map((item, bIdx) => (
                            <span key={item.label || `bd-${bIdx}`} className="text-[10px] text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded">
                              {item.label}: {item.amount > 0 ? '+' : ''}{formatCurrency(item.amount)}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Top Vendors (for purchase spikes) */}
                      {driver.top_vendors && driver.top_vendors.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {driver.top_vendors.slice(0, 2).map((v, vIdx) => (
                            <span key={v.vendor_name || `v-${vIdx}`} className="text-[10px] text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded">
                              {v.vendor_name}: +{formatCurrency(v.amount)}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Action */}
                      {driver.action && (
                        <p className="text-[10px] text-violet-400 mt-1.5 italic">
                          → {driver.action}
                        </p>
                      )}
                      
                      {/* Mode indicator for COGS */}
                      {driver.mode && (
                        <span className="text-[9px] text-slate-500 font-mono">
                          [{driver.mode}]
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Summary KPI Card
const KPICard = ({ title, value, subtitle, icon: Icon, variant = 'default', testId }) => {
  const variants = {
    default: 'border-border/50',
    success: 'border-emerald-500/30 bg-emerald-500/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    danger: 'border-rose-500/30 bg-rose-500/5',
    info: 'border-blue-500/30 bg-blue-500/5',
    primary: 'border-violet-500/30 bg-violet-500/5',
  };

  const iconVariants = {
    default: 'text-muted-foreground',
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    danger: 'text-rose-400',
    info: 'text-blue-400',
    primary: 'text-violet-400',
  };

  return (
    <Card 
      className={`bg-card/50 backdrop-blur-sm ${variants[variant]}`}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-xl font-mono font-semibold tracking-tight tabular-nums">
              {typeof value === 'number' ? formatCurrency(value) : value}
            </p>
            {subtitle && (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg bg-background/50 ${iconVariants[variant]}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};


// Cost Truth Banner — brief Part 1 / 9
// Surfaces: cogs_mode, cogs_confidence_score, actionability stance + banner.
// Colour: 🔴 not actionable, 🟡 directional, 🟢 actionable.
const CostTruthBanner = ({ cogsTruth }) => {
  if (!cogsTruth) return null;
  const {
    cogs_mode,
    cogs_engine_mode,
    cogs_confidence_score,
    cogs_confidence_level,
    actionability_stance,
    actionability_banner,
    unmapped,
    gating_reasons,
  } = cogsTruth;

  const styles = {
    ACTIONABLE: {
      box: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
      dot: 'bg-emerald-400',
      label: 'ACTIONABLE',
    },
    DIRECTIONAL: {
      box: 'bg-amber-500/10 border-amber-500/30 text-amber-200',
      dot: 'bg-amber-400',
      label: 'DIRECTIONAL',
    },
    NOT_ACTIONABLE: {
      box: 'bg-rose-500/10 border-rose-500/30 text-rose-200',
      dot: 'bg-rose-400',
      label: 'NOT ACTIONABLE',
    },
  };
  const style = styles[actionability_stance] || styles.DIRECTIONAL;
  const unmappedPct = unmapped && unmapped.total_value > 0
    ? (unmapped.unmapped_ratio * 100).toFixed(0)
    : 0;

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${style.box}`}
      data-testid="cost-truth-banner"
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-1.5 h-2.5 w-2.5 rounded-full ${style.dot} shrink-0`}
          data-testid="cost-truth-status-dot"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="font-semibold tracking-wide" data-testid="cost-truth-stance">
              {style.label}
            </span>
            <span data-testid="cost-truth-mode">
              COGS Mode: <span className="font-mono">{cogs_mode}</span>
              {cogs_engine_mode ? (
                <span className="opacity-60"> · {cogs_engine_mode}</span>
              ) : null}
            </span>
            <span data-testid="cost-truth-confidence">
              Confidence: <span className="font-mono">{cogs_confidence_score}</span>
              {' '}({cogs_confidence_level})
            </span>
            {unmapped && unmapped.unmapped_bill_count > 0 && (
              <span data-testid="cost-truth-unmapped">
                Unmapped: <span className="font-mono">{unmappedPct}%</span>
                {' '}(₹{Math.round(unmapped.unmapped_value).toLocaleString('en-IN')})
              </span>
            )}
          </div>
          <div
            className="mt-1 text-xs opacity-80"
            data-testid="cost-truth-banner-text"
          >
            {actionability_banner}
          </div>
          {gating_reasons && gating_reasons.length > 0 && (
            <div
              className="mt-1 text-[11px] opacity-60"
              data-testid="cost-truth-gating-reasons"
            >
              Gated by: {gating_reasons.join(' · ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// Confidence Strip Component
const ConfidenceStrip = ({ confidence, sourcesLastSync, cogsMeta, duplicateRisk }) => {
  const cogsMode = cogsMeta?.mode || confidence?.cogs_mode || 'PROXY';
  const valuationMethod = cogsMeta?.valuation_method || confidence?.valuation_method || 'PROXY';
  
  // New confidence score display
  const rawScore = confidence?.score || 0;
  const confidenceLevel = confidence?.level || 'LOW';
  const confidenceLabel = confidence?.label || 'Unknown';
  const primaryReason = confidence?.primary_reason || '';
  const isActionable = confidence?.is_actionable || false;
  // Cap the displayed score so "100/100 + MEDIUM + Not Actionable" is not
  // visually contradictory. The backend already downgraded level + is_actionable
  // when cogs_truth.is_actionable=false; mirror that by capping the displayed
  // score to the MEDIUM band ceiling.
  const confidenceScore = !isActionable && rawScore >= 70 ? 65 : rawScore;
  
  const getConfidenceColor = (level) => {
    switch (level) {
      case 'HIGH': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'MEDIUM': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'LOW': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };
  
  const getConfidenceIcon = (level) => {
    switch (level) {
      case 'HIGH': return CheckCircle2;
      case 'MEDIUM': return AlertTriangle;
      case 'LOW': return XCircle;
      default: return AlertTriangle;
    }
  };
  
  const ConfIcon = getConfidenceIcon(confidenceLevel);
  
  const items = [
    {
      label: 'COGS Mode',
      status: cogsMode === 'VALUATION' ? 'ok' : cogsMode === 'HYBRID' ? 'warning' : 'unavailable',
      detail: cogsMode === 'VALUATION' ? valuationMethod : cogsMode === 'HYBRID' ? 'Hybrid' : 'Proxy'
    },
    {
      label: 'Inventory',
      status: confidence?.inventory_available ? 'ok' : 'unavailable',
      detail: confidence?.inventory_freshness 
        ? format(new Date(confidence.inventory_freshness), 'MMM d, HH:mm')
        : 'N/A'
    },
    {
      label: 'POS Sync',
      status: sourcesLastSync?.pos ? 'ok' : 'stale',
      detail: sourcesLastSync?.pos 
        ? format(new Date(sourcesLastSync.pos), 'MMM d, HH:mm')
        : 'Never'
    },
    {
      label: 'Sanity Checks',
      status: (confidence?.sanity_critical_count || 0) > 0 ? 'unavailable' : 
              (confidence?.sanity_warning_count || 0) > 0 ? 'warning' : 'ok',
      detail: confidence?.sanity_check_count > 0 
        ? `${confidence.sanity_critical_count || 0}F / ${confidence.sanity_warning_count || 0}W`
        : 'All clear'
    },
    {
      label: 'Duplicate Risk',
      status: (duplicateRisk?.unresolved_count || 0) > 0 ? 'warning' : 'ok',
      detail: (duplicateRisk?.unresolved_count || 0) > 0
        ? `${duplicateRisk.unresolved_count} unresolved`
        : 'Clear'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok': return 'text-emerald-400';
      case 'warning': return 'text-amber-400';
      case 'unavailable': return 'text-rose-400';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok': return CheckCircle2;
      case 'warning': return AlertTriangle;
      case 'unavailable': return XCircle;
      default: return AlertTriangle;
    }
  };

  return (
    <div className="space-y-3">
      {/* Confidence Score Banner */}
      <div className={`p-3 rounded-lg border ${getConfidenceColor(confidenceLevel)}`} data-testid="confidence-banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ConfIcon className="w-5 h-5" />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold">{confidenceScore}</span>
                <span className="text-sm font-medium">/ 100</span>
                <Badge variant="outline" className={`text-xs ${getConfidenceColor(confidenceLevel)}`}>
                  {confidenceLevel}
                </Badge>
                <Badge 
                  data-testid="actionability-badge"
                  variant="outline" 
                  className={`text-xs ${isActionable 
                    ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' 
                    : 'text-rose-400 bg-rose-500/10 border-rose-500/30'}`}
                >
                  {isActionable ? 'Actionable' : 'Not Actionable'}
                </Badge>
              </div>
              <p className="text-xs opacity-80">{confidenceLabel}</p>
            </div>
          </div>
          <div className="text-right max-w-[40%]">
            <p className="text-xs opacity-70">{primaryReason}</p>
          </div>
        </div>
        {/* Actionability callout when not actionable */}
        {!isActionable && (
          <div className="mt-2 pt-2 border-t border-current/20">
            <p className="text-xs opacity-80 flex items-center gap-1.5" data-testid="actionability-warning">
              <AlertTriangle className="w-3 h-3" />
              Insight paused due to low data confidence. Verify stock inputs before acting on margin signals.
            </p>
          </div>
        )}
        {/* Duplicate expense risk callout */}
        {(duplicateRisk?.unresolved_count || 0) > 0 && (
          <div className="mt-2 pt-2 border-t border-current/20" data-testid="duplicate-risk-callout">
            <p className="text-xs opacity-80 flex items-center gap-1.5">
              <Copy className="w-3 h-3" />
              {duplicateRisk.unresolved_count} bill(s) flagged as potential duplicates 
              ({new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(duplicateRisk.unresolved_amount)} at risk). 
              Resolve in Expense Capture.
            </p>
            {duplicateRisk.excluded_count > 0 && (
              <p className="text-xs opacity-60 mt-1 ml-5">
                {duplicateRisk.excluded_count} duplicate(s) excluded from P&L 
                ({new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(duplicateRisk.excluded_amount)})
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Status Items */}
      <div className="flex flex-wrap gap-4 p-3 bg-card/30 rounded-lg border border-border/30">
        {items.map((item, idx) => {
          const StatusIcon = getStatusIcon(item.status);
          return (
            <div key={`item-${item?.label || item?.category || idx}`} className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${getStatusColor(item.status)}`} />
              <span className="text-xs text-muted-foreground">{item.label}:</span>
              <span className="text-xs font-medium">{item.detail}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sanity Checks Panel (v2 - using new confidence layer format)
const SanityChecksPanel = ({ checks, cogsMeta }) => {
  if (!checks || checks.length === 0) return null;
  
  const getSeverityIcon = (status) => {
    switch (status) {
      case 'FAIL': return XCircle;
      case 'WARN': return AlertTriangle;
      case 'PASS': return CheckCircle2;
      case 'INFO': return Info;
      default: return AlertTriangle;
    }
  };
  
  const getSeverityColor = (status) => {
    switch (status) {
      case 'FAIL': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      case 'WARN': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'PASS': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'INFO': return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };
  
  // Filter to show only non-PASS checks by default
  const issueChecks = checks.filter(c => c.status !== 'PASS');
  const passChecks = checks.filter(c => c.status === 'PASS');

  return (
    <Card className="bg-card/30 border-border/30" data-testid="sanity-checks-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            Sanity Checks
          </CardTitle>
          <div className="flex items-center gap-2">
            {cogsMeta && (
              <Badge variant="outline" className="text-xs">
                {cogsMeta.mode}: {cogsMeta.display_label}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {issueChecks.length} issue{issueChecks.length !== 1 ? 's' : ''} / {checks.length} checks
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {/* Show issues first */}
        {issueChecks.map((check, idx) => {
          const Icon = getSeverityIcon(check.status);
          return (
            <div 
              key={`check-${check.code}-${idx}`} 
              className={`p-2.5 rounded-lg border ${getSeverityColor(check.status)}`}
              data-testid={`sanity-check-${check.code}`}
            >
              <div className="flex items-start gap-2">
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">{check.label}</span>
                    <Badge variant="outline" className="text-[9px]">{check.status}</Badge>
                  </div>
                  <p className="text-xs opacity-80 mt-0.5">{check.message}</p>
                  {check.value !== null && check.value !== undefined && (
                    <p className="text-[10px] opacity-60 mt-1 font-mono">
                      Value: {typeof check.value === 'object' ? JSON.stringify(check.value) : check.value}
                      {check.threshold !== null && ` | Threshold: ${check.threshold}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Collapsed pass checks */}
        {passChecks.length > 0 && (
          <div className="pt-2 border-t border-border/30">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              {passChecks.length} check{passChecks.length !== 1 ? 's' : ''} passed
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Margin Alerts Panel - Context-aware operational signals
const MarginAlertsPanel = ({ dateFrom, dateTo }) => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const response = await accountsAPI.getPnLAlerts({ date_from: dateFrom, date_to: dateTo });
      setAlerts(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  if (loading && !alerts) {
    return (
      <Card className="bg-card/30 border-border/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Computing alerts...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Phase 6 — UI honesty: render the panel even when no alerts fired,
  // so blocked signals (cost/cash data unavailable) are visible to the
  // operator instead of silently disappearing.
  if (!alerts) return null;
  const blockedSignals = alerts.blocked_signals || [];
  const warnings = alerts.warnings || [];
  const alertsState = alerts.state || 'ACTIVE';
  const hasAnyAlert = (alerts.alert_count || 0) > 0;
  const hasBlocked = blockedSignals.length > 0;
  if (!hasAnyAlert && !hasBlocked) return null;

  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      case 'WARNING': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'INFO': return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL': return XCircle;
      case 'WARNING': return AlertTriangle;
      case 'INFO': return Info;
      default: return Info;
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'AT_RISK': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      case 'WATCH': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'STABLE': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      default: return 'text-slate-400';
    }
  };

  const alertsList = alerts.alerts || [];
  const displayAlerts = expanded ? alertsList : alertsList.slice(0, 3);

  return (
    <Card className="bg-card/30 border-border/30" data-testid="margin-alerts-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" />
            Margin Alerts
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge 
              data-testid="alerts-status-badge"
              variant="outline" 
              className={`text-xs ${getStatusStyle(alerts.overall_status)}`}
            >
              {alerts.overall_status}
            </Badge>
            {alerts.critical_count > 0 && (
              <Badge variant="outline" className="text-xs text-rose-400 border-rose-500/30">
                {alerts.critical_count} critical
              </Badge>
            )}
            {alerts.warning_count > 0 && (
              <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">
                {alerts.warning_count} warning
              </Badge>
            )}
            {!alerts.is_actionable && (
              <Badge variant="outline" className="text-xs text-rose-400 border-rose-500/30" data-testid="alerts-not-actionable">
                Not Actionable
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {/* Phase 6 — UI honesty: show WHY some alerts are missing.
            Backed by alerts.blocked_signals from the Signal Validity layer. */}
        {hasBlocked && (
          <div
            className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5"
            data-testid="alerts-blocked-strip"
          >
            <div className="flex items-start gap-2.5">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-amber-200">
                    {alertsState === 'BLOCKED_BY_TRUTH_CONTRACT'
                      ? 'Alerts unavailable — data not decision-grade'
                      : `${blockedSignals.length} alert${blockedSignals.length !== 1 ? 's' : ''} suppressed by truth contract`}
                  </span>
                  <Badge variant="outline" className="text-[9px] text-amber-300 border-amber-500/30">
                    {alertsState}
                  </Badge>
                </div>
                <ul className="mt-1.5 space-y-0.5">
                  {blockedSignals.map((b, i) => (
                    <li
                      key={`${b.signal}-${i}`}
                      className="text-[11px] text-amber-200/80"
                      data-testid={`alerts-blocked-${b.signal}`}
                    >
                      <span className="font-mono text-amber-200/90">{b.signal}</span>
                      {b.reason_human ? (
                        <span className="ml-1.5">— {b.reason_human}</span>
                      ) : b.reason ? (
                        <span className="ml-1.5 font-mono opacity-70">— {b.reason}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {warnings.length > 0 && (
                  <p className="text-[10px] mt-1.5 opacity-70 text-amber-200/70">
                    {warnings.join(' · ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {displayAlerts.map((alert, idx) => {
          const Icon = getSeverityIcon(alert.severity);
          const isGated = !alert.is_actionable;
          return (
            <div 
              key={alert.alert_id || idx}
              className={`p-3 rounded-lg border ${getSeverityStyle(alert.severity)} ${isGated ? 'opacity-60' : ''}`}
              data-testid={`alert-${alert.alert_type}`}
            >
              <div className="flex items-start gap-2.5">
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold">{alert.title}</span>
                    <Badge variant="outline" className="text-[9px]">{alert.severity}</Badge>
                    {alert.short_label && (
                      <span className="text-[10px] font-mono opacity-70">{alert.short_label}</span>
                    )}
                    {isGated && (
                      <Badge variant="outline" className="text-[9px] text-rose-400 border-rose-500/30">
                        Verify first
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs opacity-80 mt-1">{alert.message}</p>
                  {alert.recommended_action && (
                    <p className="text-[10px] mt-1.5 opacity-60 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {alert.recommended_action}
                    </p>
                  )}
                  {alert.driver_summary && (
                    <p className="text-[10px] mt-1 opacity-50 italic">
                      Driver: {alert.driver_summary}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {alertsList.length > 3 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-xs"
            onClick={() => setExpanded(!expanded)}
            data-testid="alerts-expand-btn"
          >
            {expanded ? 'Show less' : `Show ${alertsList.length - 3} more alert${alertsList.length - 3 !== 1 ? 's' : ''}`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Operating Pulse Preview Panel - WhatsApp-style message preview
const PulsePreviewPanel = ({ dateFrom, dateTo }) => {
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  const fetchPulse = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const response = await accountsAPI.getPnLPulse({ date_from: dateFrom, date_to: dateTo });
      setPulse(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await accountsAPI.getPulseLogs({ limit: 20 });
      setLogs(response.data?.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  }, []);

  useEffect(() => {
    if (showPanel) fetchPulse();
  }, [showPanel, fetchPulse]);

  useEffect(() => {
    if (showLogs) fetchLogs();
  }, [showLogs, fetchLogs]);

  const handleTriggerTest = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const response = await accountsAPI.triggerTestPulse({ date_from: dateFrom, date_to: dateTo });
      setTriggerResult(response.data);
      toast.success('Test pulse triggered');
      fetchLogs();
    } catch (err) {
      toast.error('Failed to trigger test pulse');
    } finally {
      setTriggering(false);
    }
  };

  const handleTriggerDaily = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const response = await accountsAPI.triggerDailyPulse({ date_from: dateFrom, date_to: dateTo });
      setTriggerResult(response.data);
      const sent = response.data?.decision?.should_send;
      if (sent) toast.success('Daily pulse sent');
      else toast.info(`Daily pulse suppressed: ${response.data?.decision?.suppress_reason || 'Unknown'}`);
      fetchLogs();
    } catch (err) {
      toast.error('Failed to run daily pulse');
    } finally {
      setTriggering(false);
    }
  };

  const handleTriggerEvents = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const response = await accountsAPI.triggerEventPulse({ date_from: dateFrom, date_to: dateTo });
      setTriggerResult(response.data);
      const sent = response.data?.decision?.should_send;
      if (sent) toast.success(`Event pulse fired: ${response.data?.decision?.trigger_reason}`);
      else toast.info(`No event triggers: ${response.data?.decision?.suppress_reason || 'No conditions met'}`);
      fetchLogs();
    } catch (err) {
      toast.error('Failed to run event scan');
    } finally {
      setTriggering(false);
    }
  };

  const getConfBadgeStyle = (level) => {
    switch (level) {
      case 'HIGH': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40';
      case 'MEDIUM': return 'bg-amber-500/20 text-amber-400 border-amber-500/40';
      case 'LOW': return 'bg-rose-500/20 text-rose-400 border-rose-500/40';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/40';
    }
  };

  const getDirectionIcon = (dir) => {
    if (dir === 'up') return <ArrowUp className="w-3 h-3 text-rose-400" />;
    if (dir === 'down') return <ArrowDown className="w-3 h-3 text-emerald-400" />;
    return <Minus className="w-3 h-3 text-slate-400" />;
  };

  const getDeliveryBadge = (status) => {
    const styles = {
      SENT: 'text-emerald-400 border-emerald-500/30',
      MOCK: 'text-blue-400 border-blue-500/30',
      SUPPRESSED: 'text-amber-400 border-amber-500/30',
      FAILED: 'text-rose-400 border-rose-500/30',
      PENDING: 'text-slate-400 border-slate-500/30',
    };
    return styles[status] || styles.PENDING;
  };

  return (
    <div data-testid="pulse-preview-section">
      {!showPanel && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          onClick={() => setShowPanel(true)}
          data-testid="pulse-toggle-btn"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          Operating Pulse
        </Button>
      )}

      {showPanel && (
        <Card className="bg-card/30 border-border/30 overflow-hidden" data-testid="pulse-preview-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-emerald-400" />
                Operating Pulse
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-500/30">
                  MOCK DELIVERY
                </Badge>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowPanel(false)}>
                  Close
                </Button>
              </div>
            </div>
            {/* Tab Switch */}
            <div className="flex gap-1 mt-2">
              {['preview', 'triggers', 'logs'].map(tab => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs capitalize"
                  onClick={() => { setActiveTab(tab); if (tab === 'logs') setShowLogs(true); }}
                  data-testid={`pulse-tab-${tab}`}
                >
                  {tab}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* PREVIEW TAB */}
            {activeTab === 'preview' && (
              <>
                {loading && !pulse ? (
                  <div className="flex items-center gap-2 p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Generating pulse...</span>
                  </div>
                ) : pulse ? (
                  <div className="space-y-4">
                    {/* Truth Surface — mirrors the P&L page so operators see
                        identical "Sales Source + Reconciliation" signals
                        wherever sales numbers render. */}
                    {pulse.truth_surface && (
                      <PulseTruthSurface
                        truthSurface={pulse.truth_surface}
                        testIdPrefix="daily-pulse"
                      />
                    )}

                    {/* WhatsApp Phone Preview */}
                    <div className="mx-auto max-w-sm">
                      <div className="bg-[#0b141a] rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                        <div className="bg-[#1f2c34] px-4 py-2.5 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">1OS</div>
                          <div>
                            <p className="text-sm font-medium text-white">1OS Accounts</p>
                            <p className="text-[10px] text-slate-400">Daily Pulse</p>
                          </div>
                        </div>
                        <div className="p-3 min-h-[160px]">
                          <div className="bg-[#005c4b] rounded-lg rounded-tl-none p-3 max-w-[95%] shadow-sm">
                            <pre className="text-[12px] text-white whitespace-pre-wrap font-sans leading-relaxed" data-testid="pulse-whatsapp-message">
                              {pulse.whatsapp_message}
                            </pre>
                            <p className="text-right text-[9px] text-emerald-200/60 mt-1">
                              {new Date(pulse.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Breakdown */}
                    <div className="space-y-3 pt-2 border-t border-border/20">
                      <p className="text-xs text-muted-foreground font-medium">Pulse Breakdown</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${getConfBadgeStyle(pulse.confidence_level)}`} data-testid="pulse-confidence-badge">
                          Score: {pulse.confidence_score} ({pulse.confidence_level})
                        </Badge>
                        <Badge variant="outline" className={`text-xs ${pulse.is_actionable ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : 'text-rose-400 bg-rose-500/10 border-rose-500/30'}`} data-testid="pulse-actionability-badge">
                          {pulse.is_actionable ? 'Actionable' : 'Not Actionable'}
                        </Badge>
                      </div>

                      {pulse.top_signals?.length > 0 && (
                        <div data-testid="pulse-signals">
                          <p className="text-[10px] text-muted-foreground mb-1.5">KEY SIGNALS</p>
                          {pulse.top_signals.map((s, i) => (
                            <div key={`sig-${i}`} className="flex items-center gap-2 py-1">
                              {getDirectionIcon(s.direction)}
                              <span className="text-xs">{s.metric}: <strong>{s.value}</strong></span>
                              {s.delta && <span className="text-[10px] text-muted-foreground">{s.delta}</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      {pulse.top_drivers?.length > 0 && (
                        <div data-testid="pulse-drivers">
                          <p className="text-[10px] text-muted-foreground mb-1.5">WHY</p>
                          {pulse.top_drivers.map((d, i) => (
                            <p key={`drv-${i}`} className="text-xs text-muted-foreground italic">{d.summary}</p>
                          ))}
                        </div>
                      )}

                      {pulse.top_alerts?.length > 0 && (
                        <div data-testid="pulse-alerts">
                          <p className="text-[10px] text-muted-foreground mb-1.5">INCLUDED ALERTS</p>
                          {pulse.top_alerts.map((a, i) => (
                            <div key={`alt-${i}`} className="flex items-center gap-2 py-0.5">
                              <Badge variant="outline" className={`text-[9px] ${a.severity === 'CRITICAL' ? 'text-rose-400 border-rose-500/30' : a.severity === 'WARNING' ? 'text-amber-400 border-amber-500/30' : 'text-blue-400 border-blue-500/30'}`}>{a.severity}</Badge>
                              <span className="text-xs">{a.title}</span>
                              {!a.is_actionable && <span className="text-[9px] text-rose-400">gated</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className={`p-2.5 rounded-lg border ${pulse.action?.is_gated ? 'bg-rose-500/5 border-rose-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`} data-testid="pulse-action">
                        <p className="text-[10px] text-muted-foreground mb-1">RECOMMENDED ACTION</p>
                        <p className="text-xs">{pulse.action?.is_gated ? '🚫 ' : '⚡ '}{pulse.action?.text}</p>
                      </div>

                      {/* Phase 5 — Narrative gating: render blocked stories
                          explicitly so insights NEVER disappear silently. */}
                      {pulse.blocked_stories?.length > 0 && (
                        <div
                          className="space-y-1.5"
                          data-testid="pulse-blocked-stories"
                        >
                          <p className="text-[10px] text-amber-300/80 font-semibold uppercase tracking-wide flex items-center gap-1.5">
                            <Info className="w-3 h-3" />
                            Insights unavailable — data not decision-grade
                          </p>
                          {pulse.blocked_stories.map((bs, i) => (
                            <div
                              key={`${bs.type}-${i}`}
                              className="p-2 rounded-md border border-amber-500/20 bg-amber-500/5"
                              data-testid={`pulse-blocked-${bs.type}`}
                            >
                              <p className="text-[11px] font-medium text-amber-200">
                                {bs.title}
                              </p>
                              {bs.text && (
                                <p className="text-[10px] text-amber-200/70 mt-0.5">
                                  {bs.text}
                                </p>
                              )}
                              {(bs.reason_human || bs.reason) && (
                                <p className="text-[9px] text-amber-300/60 mt-0.5">
                                  {bs.reason_human ? (
                                    <span>{bs.reason_human}</span>
                                  ) : (
                                    <span className="font-mono">reason: {bs.reason}</span>
                                  )}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-4">No pulse data available.</p>
                )}
              </>
            )}

            {/* TRIGGERS TAB */}
            {activeTab === 'triggers' && (
              <div className="space-y-4 pt-2">
                <p className="text-xs text-muted-foreground">Test trigger logic. Evaluates conditions, applies suppression, and logs the result.</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={handleTriggerTest} disabled={triggering} data-testid="pulse-trigger-test-btn">
                    {triggering ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                    Test Pulse
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={handleTriggerDaily} disabled={triggering} data-testid="pulse-trigger-daily-btn">
                    {triggering ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Daily Run
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={handleTriggerEvents} disabled={triggering} data-testid="pulse-trigger-events-btn">
                    {triggering ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                    Event Scan
                  </Button>
                </div>

                {triggerResult && (
                  <div className="space-y-2 p-3 rounded-lg bg-card/50 border border-border/30" data-testid="pulse-trigger-result">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={triggerResult.decision?.should_send ? 'text-emerald-400 border-emerald-500/30' : 'text-amber-400 border-amber-500/30'}>
                        {triggerResult.decision?.should_send ? 'SENT' : 'SUPPRESSED'}
                      </Badge>
                      {triggerResult.decision?.trigger_type && (
                        <Badge variant="outline" className="text-[10px]">
                          {triggerResult.decision.trigger_type}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {triggerResult.decision?.should_send
                        ? triggerResult.decision.trigger_reason
                        : triggerResult.decision?.suppress_reason}
                    </p>
                    {triggerResult.decision?.matched_trigger_types?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        <span className="text-[10px] text-muted-foreground">Matched:</span>
                        {triggerResult.decision.matched_trigger_types.map((t, i) => (
                          <Badge key={`mt-${i}`} variant="outline" className="text-[9px]">{t}</Badge>
                        ))}
                      </div>
                    )}
                    {triggerResult.delivery && (
                      <p className="text-[10px] text-muted-foreground">
                        Delivery: {triggerResult.delivery.status} via {triggerResult.delivery.channel || triggerResult.delivery.status}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* LOGS TAB */}
            {activeTab === 'logs' && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Recent pulse activity</p>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchLogs}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                </div>
                {logs.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-4 text-center">No pulse logs yet. Trigger a test pulse to get started.</p>
                ) : (
                  <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="pulse-logs-list">
                    {logs.map((log, i) => (
                      <div key={log.id || i} className="p-2.5 rounded-lg bg-card/30 border border-border/20 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={`text-[9px] ${getDeliveryBadge(log.delivery_status)}`}>
                            {log.delivery_status}
                          </Badge>
                          <Badge variant="outline" className="text-[9px]">
                            {log.trigger_type}
                          </Badge>
                          {log.confidence_score != null && (
                            <span className="text-[10px] text-muted-foreground">
                              Conf: {log.confidence_score}
                            </span>
                          )}
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            {log.created_at ? new Date(log.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground">{log.trigger_reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// EOD Close Pulse Preview Panel
const EodClosePulsePanel = ({ dateFrom }) => {
  const [eod, setEod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [activeView, setActiveView] = useState('whatsapp');

  const fetchEod = useCallback(async () => {
    if (!dateFrom) return;
    setLoading(true);
    try {
      const response = await accountsAPI.getEodPulse({ date: dateFrom });
      setEod(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  }, [dateFrom]);

  useEffect(() => {
    if (showPanel) fetchEod();
  }, [showPanel, fetchEod]);

  const getCloseStateBadge = (state) => {
    switch (state) {
      case 'VERIFIED': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'PROVISIONAL': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'INCOMPLETE': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  const fmtInr = (val) => {
    if (!val && val !== 0) return '₹0';
    if (Math.abs(val) >= 100000) return `₹${(val/100000).toFixed(1)}L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  return (
    <div data-testid="eod-pulse-section">
      {!showPanel && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          onClick={() => setShowPanel(true)}
          data-testid="eod-pulse-toggle-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          End-of-Day Close Pulse
        </Button>
      )}

      {showPanel && (
        <Card className="bg-card/30 border-border/30 overflow-hidden" data-testid="eod-pulse-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-400" />
                EOD Close Pulse
                {eod && (
                  <Badge variant="outline" className={`text-[10px] ${getCloseStateBadge(eod.close_status?.close_state)}`}>
                    {eod.close_status?.close_state}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-500/30">MOCK</Badge>
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowPanel(false)}>Close</Button>
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              {['whatsapp', 'breakdown'].map(v => (
                <Button key={v} variant={activeView === v ? 'secondary' : 'ghost'} size="sm" className="h-7 text-xs capitalize"
                  onClick={() => setActiveView(v)} data-testid={`eod-view-${v}`}>
                  {v === 'whatsapp' ? 'WhatsApp Preview' : 'Detail Breakdown'}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {loading && !eod ? (
              <div className="flex items-center gap-2 p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Generating EOD close...</span>
              </div>
            ) : eod ? (
              <>
                {/* Truth Surface — identical to Daily Pulse + P&L page so the
                    operator sees one coherent honesty signal across every
                    consumer of sc_transactions. */}
                {eod.truth_surface && (
                  <div className="mb-3">
                    <PulseTruthSurface
                      truthSurface={eod.truth_surface}
                      testIdPrefix="eod-pulse"
                    />
                  </div>
                )}

                {/* WhatsApp Preview */}
                {activeView === 'whatsapp' && (
                  <div className="mx-auto max-w-sm mt-2">
                    <div className="bg-[#0b141a] rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                      <div className="bg-[#1f2c34] px-4 py-2.5 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">1OS</div>
                        <div>
                          <p className="text-sm font-medium text-white">1OS Accounts</p>
                          <p className="text-[10px] text-slate-400">EOD Close • {eod.outlet_name}</p>
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="bg-[#005c4b] rounded-lg rounded-tl-none p-3 max-w-[95%] shadow-sm">
                          <pre className="text-[11px] text-white whitespace-pre-wrap font-sans leading-relaxed" data-testid="eod-whatsapp-message">
                            {eod.plain_text_message}
                          </pre>
                          <p className="text-right text-[9px] text-emerald-200/60 mt-1">
                            {new Date(eod.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detail Breakdown */}
                {activeView === 'breakdown' && (
                  <div className="space-y-3 mt-2">
                    {/* Sales Summary Card */}
                    <div className="p-3 rounded-lg bg-card/50 border border-border/20" data-testid="eod-sales-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">TODAY'S SALES</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><p className="text-[10px] text-muted-foreground">Gross Sales</p><p className="text-sm font-semibold">{fmtInr(eod.sales_summary?.gross_sales)}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">Net Sales</p><p className="text-sm font-semibold">{fmtInr(eod.sales_summary?.net_sales)}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">Bills</p><p className="text-sm font-semibold">{eod.sales_summary?.bills_count || 0}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">ABV</p><p className="text-sm font-semibold">{fmtInr(eod.sales_summary?.abv)}</p></div>
                      </div>
                    </div>

                    {/* Revenue Breakdown Card */}
                    <div className="p-3 rounded-lg bg-card/50 border border-border/20" data-testid="eod-revenue-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">COLLECTIONS MIX</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ['Cash', eod.revenue_breakdown?.cash],
                          ['Card', eod.revenue_breakdown?.card],
                          ['UPI', eod.revenue_breakdown?.upi],
                          ['Online Zomato', eod.revenue_breakdown?.online_zomato],
                          ['Online Swiggy', eod.revenue_breakdown?.online_swiggy],
                          ['Dine-In Zomato', eod.revenue_breakdown?.dine_in_zomato],
                          ['Dine-In Swiggy', eod.revenue_breakdown?.dine_in_swiggy],
                          ['Non-Chargeable', eod.revenue_breakdown?.non_chargeable_sale],
                        ].filter(([_, v]) => v > 0).map(([label, val], i) => (
                          <div key={`rev-${i}`}><p className="text-[10px] text-muted-foreground">{label}</p><p className="text-xs font-semibold">{fmtInr(val)}</p></div>
                        ))}
                      </div>
                    </div>

                    {/* Cash Movement Card */}
                    <div className="p-3 rounded-lg bg-card/50 border border-border/20" data-testid="eod-cash-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">CASH MOVEMENT</p>
                      <div className="space-y-1.5">
                        {[
                          ['Cash Collected', eod.cash_movement?.daily_cash_collected, 'text-emerald-400'],
                          ['Cash Expense', eod.cash_movement?.daily_cash_expense, 'text-rose-400'],
                          ['Cash Incentive', eod.cash_movement?.daily_cash_incentive, 'text-amber-400'],
                        ].map(([label, val, color], i) => (
                          <div key={`cash-${i}`} className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">{label}</span>
                            <span className={`text-xs font-semibold ${color}`}>{fmtInr(val || 0)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-1.5 border-t border-border/20">
                          <span className="text-xs font-medium">Net Cash Movement</span>
                          <span className="text-sm font-bold">{fmtInr(eod.cash_movement?.daily_net_cash_movement)}</span>
                        </div>
                      </div>
                    </div>

                    {/* P&L Card */}
                    <div className="p-3 rounded-lg bg-card/50 border border-border/20" data-testid="eod-pnl-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">PROVISIONAL P&L</p>
                      <div className="space-y-1.5">
                        {[
                          ['Net Sales', eod.pnl_summary?.net_sales],
                          ['COGS', eod.pnl_summary?.cogs],
                          ['Opex', eod.pnl_summary?.opex],
                          ['Cash Expense', eod.pnl_summary?.daily_cash_expense],
                        ].filter(([_, v]) => v > 0).map(([label, val], i) => (
                          <div key={`pnl-${i}`} className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">{label}</span>
                            <span className="text-xs font-semibold">{fmtInr(val)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center pt-1.5 border-t border-border/20">
                          <span className="text-xs font-medium">Operating Profit</span>
                          <span className={`text-sm font-bold ${(eod.pnl_summary?.operating_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {fmtInr(eod.pnl_summary?.operating_profit)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* MTD Snapshot Card */}
                    <div className="p-3 rounded-lg bg-card/50 border border-border/20" data-testid="eod-mtd-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">MTD SNAPSHOT</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><p className="text-[10px] text-muted-foreground">Gross Sales</p><p className="text-xs font-semibold">{fmtInr(eod.mtd_summary?.mtd_gross_sales)}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">Net Sales</p><p className="text-xs font-semibold">{fmtInr(eod.mtd_summary?.mtd_net_sales)}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">Cash Collected</p><p className="text-xs font-semibold">{fmtInr(eod.mtd_summary?.mtd_cash_collected)}</p></div>
                        <div><p className="text-[10px] text-muted-foreground">Cash Expense</p><p className="text-xs font-semibold">{fmtInr(eod.mtd_summary?.mtd_cash_expense)}</p></div>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-border/20">
                        <span className="text-xs font-medium">Cash in Hand</span>
                        <div className="text-right">
                          <span className="text-sm font-bold">{fmtInr(eod.mtd_summary?.mtd_cash_in_hand)}</span>
                          {eod.mtd_summary?.is_system_tracked && (
                            <p className="text-[9px] text-muted-foreground">system-tracked</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Close Status Card */}
                    <div className={`p-3 rounded-lg border ${getCloseStateBadge(eod.close_status?.close_state)}`} data-testid="eod-close-card">
                      <p className="text-[10px] text-muted-foreground font-medium mb-2">CLOSE STATUS</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${getCloseStateBadge(eod.close_status?.close_state)}`}>
                          {eod.close_status?.close_state}
                        </Badge>
                        <span className="text-xs">Confidence: {eod.close_status?.confidence_score} ({eod.close_status?.confidence_level})</span>
                      </div>
                      <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
                        <span>POS: {eod.close_status?.pos_sync_status}</span>
                        <span>COGS: {eod.close_status?.inventory_mode}</span>
                        <span>Cash Entry: {eod.close_status?.cash_entry_status}</span>
                      </div>
                      {eod.close_status?.close_note && (
                        <p className="text-xs mt-1.5 opacity-80">{eod.close_status.close_note}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground p-4">No EOD data available.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// COGS Detail Block
const COGSDetailBlock = ({ title, icon: Icon, data, salesAmount, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'text-emerald-400 bg-emerald-500/10',
    violet: 'text-violet-400 bg-violet-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    rose: 'text-rose-400 bg-rose-500/10',
  };

  return (
    <AccordionItem value={title.toLowerCase()} className="border-border/30">
      <AccordionTrigger className="hover:no-underline py-3 px-4">
        <div className="flex items-center justify-between w-full pr-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="font-medium">{title}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-lg font-semibold">
              {formatCurrency(data?.total || 0)}
            </span>
            <Badge variant={data?.pct_of_sales > 35 ? 'destructive' : 'secondary'}>
              {formatPercent(data?.pct_of_sales)}
            </Badge>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="p-3 bg-card/50 rounded-lg">
            <p className="text-muted-foreground text-xs mb-1">Opening Stock</p>
            <p className="font-mono font-medium">{formatCurrency(data?.opening_stock)}</p>
          </div>
          <div className="p-3 bg-card/50 rounded-lg">
            <p className="text-muted-foreground text-xs mb-1">Purchases</p>
            <p className="font-mono font-medium">{formatCurrency(data?.purchases)}</p>
          </div>
          {data?.bar_cost !== undefined && (
            <div className="p-3 bg-card/50 rounded-lg">
              <p className="text-muted-foreground text-xs mb-1">Bar Cost</p>
              <p className="font-mono font-medium">{formatCurrency(data?.bar_cost)}</p>
            </div>
          )}
          {data?.transfers_in !== undefined && (
            <div className="p-3 bg-card/50 rounded-lg">
              <p className="text-muted-foreground text-xs mb-1">Transfers In</p>
              <p className="font-mono font-medium text-emerald-400">+{formatCurrency(data?.transfers_in)}</p>
            </div>
          )}
          {data?.transfers_out !== undefined && (
            <div className="p-3 bg-card/50 rounded-lg">
              <p className="text-muted-foreground text-xs mb-1">Transfers Out</p>
              <p className="font-mono font-medium text-rose-400">-{formatCurrency(data?.transfers_out)}</p>
            </div>
          )}
          <div className="p-3 bg-card/50 rounded-lg">
            <p className="text-muted-foreground text-xs mb-1">Closing Stock</p>
            <p className="font-mono font-medium">{formatCurrency(data?.closing_stock)}</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-border/30 flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            Formula: Opening + Purchases - Closing = COGS
          </span>
          <span className="text-xs text-muted-foreground">
            Sales: {formatCurrency(salesAmount)}
          </span>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

// Date Range Quick Selectors
// ─── Target Variance Strip ──────────────────────────────────

const TargetVarianceStrip = ({ dateFrom, dateTo }) => {
  const { user } = useAuth();
  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTargets, setEditTargets] = useState({});

  const fetchVariance = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await accountsAPI.getTargetVariance({ date_from: dateFrom, date_to: dateTo });
      setVariance(res.data);
    } catch (err) {
      // Distinguish fetch failure from "no target configured" — the two used
      // to collapse into the same empty-state, hiding auth/network/upstream
      // problems behind a friendly CTA. Operators couldn't tell which state
      // they were in.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let code = 'FETCH_ERROR';
      let message = 'Unable to load target variance';
      if (status === 401 || status === 403) {
        code = 'AUTH_ERROR';
        message = 'Session expired or unauthorized — please sign in again';
      } else if (status >= 500) {
        code = 'SERVER_ERROR';
        message = `Variance service error (${status})`;
      } else if (err?.code === 'ERR_NETWORK' || err?.message?.includes('Network')) {
        code = 'NETWORK_ERROR';
        message = 'Network error — variance endpoint unreachable';
      } else if (detail) {
        message = String(detail);
      }
      setFetchError({ code, message, status });
      setVariance(null);
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchVariance(); }, [fetchVariance]);

  const openEditor = () => {
    const t = variance?.targets || {};
    setEditTargets({
      net_sales: t.net_sales || '',
      food_cost_pct: t.food_cost_pct || '',
      gross_margin_pct: t.gross_margin_pct || '',
      labor_cost_pct: t.labor_cost_pct || '',
    });
    setEditing(true);
  };

  const saveTargets = async () => {
    try {
      const month = variance?.month || format(new Date(), 'yyyy-MM');
      const targets = {};
      if (editTargets.net_sales) targets.net_sales = parseFloat(editTargets.net_sales);
      if (editTargets.food_cost_pct) targets.food_cost_pct = parseFloat(editTargets.food_cost_pct);
      if (editTargets.gross_margin_pct) targets.gross_margin_pct = parseFloat(editTargets.gross_margin_pct);
      if (editTargets.labor_cost_pct) targets.labor_cost_pct = parseFloat(editTargets.labor_cost_pct);
      await accountsAPI.setMonthlyTargets({ month, targets });
      toast.success('Monthly targets saved');
      setEditing(false);
      fetchVariance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save targets');
    }
  };

  if (loading) return <Skeleton className="h-36 rounded-xl" data-testid="target-strip-loading" />;

  // FETCH_ERROR — distinct from "no target set". Operators can tell whether
  // the strip is empty because they haven't configured targets vs because the
  // variance endpoint failed (auth / network / 5xx).
  if (fetchError) {
    return (
      <Card className="bg-rose-500/5 border border-rose-500/30" data-testid="target-strip-error">
        <CardContent className="py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-2 rounded-lg bg-rose-500/10 shrink-0">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-rose-200 font-medium" data-testid="target-strip-error-title">
                Target variance unavailable
              </p>
              <p className="text-[11px] text-rose-300/80 truncate" data-testid="target-strip-error-detail">
                {fetchError.message}
                {fetchError.status ? ` · HTTP ${fetchError.status}` : ''}
                {` · ${fetchError.code}`}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchVariance}
            className="text-xs border-rose-500/40 text-rose-200 hover:bg-rose-500/10 shrink-0"
            data-testid="target-strip-retry-btn"
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // NO_TARGET — explicit. Show month + outlet so wrong-outlet routing is
  // visually obvious (operators were mistaking outlet drift for the target
  // having "disappeared").
  if (!variance?.has_target) {
    const monthLabel = (() => {
      const m = variance?.month || format(new Date(dateFrom || new Date()), 'yyyy-MM');
      try { return format(new Date(`${m}-01T00:00:00`), 'MMMM yyyy'); } catch (_e) { return m; }
    })();
    const outletId = variance?.outlet_id || user?.outlet_id || '';
    const outletShort = outletId ? outletId.slice(0, 8) : 'unknown';
    return (
      <Card className="bg-zinc-900/60 border-zinc-800 border-dashed" data-testid="target-strip-empty">
        <CardContent className="py-4">
          {editing ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-300 font-medium">Set Monthly Targets</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="text-xs text-zinc-400"><X className="w-3 h-3 mr-1" />Cancel</Button>
                  <Button size="sm" onClick={saveTargets} className="bg-amber-500 hover:bg-amber-600 text-black text-xs" data-testid="save-targets-btn"><Save className="w-3 h-3 mr-1" />Save</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] text-zinc-500">Net Sales Target</Label>
                  <Input type="number" value={editTargets.net_sales} onChange={e => setEditTargets(p => ({...p, net_sales: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" placeholder="e.g. 1800000" data-testid="input-target-sales" />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Food Cost % Target</Label>
                  <Input type="number" step="0.5" value={editTargets.food_cost_pct} onChange={e => setEditTargets(p => ({...p, food_cost_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" placeholder="e.g. 30" data-testid="input-target-food" />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Gross Margin % Target</Label>
                  <Input type="number" step="0.5" value={editTargets.gross_margin_pct} onChange={e => setEditTargets(p => ({...p, gross_margin_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" placeholder="e.g. 70" data-testid="input-target-margin" />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Labor Cost % (optional)</Label>
                  <Input type="number" step="0.5" value={editTargets.labor_cost_pct} onChange={e => setEditTargets(p => ({...p, labor_cost_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" placeholder="e.g. 18" data-testid="input-target-labor" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg bg-amber-500/10 shrink-0"><Target className="w-4 h-4 text-amber-400" /></div>
                <div className="min-w-0">
                  <p className="text-sm text-zinc-300" data-testid="target-strip-empty-title">
                    No target configured for {monthLabel}
                  </p>
                  <p className="text-[11px] text-zinc-500 truncate" data-testid="target-strip-empty-outlet">
                    Outlet {outletShort} · Set targets to track progress against your business plan
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={openEditor} className="text-xs border-zinc-700 shrink-0" data-testid="set-targets-btn">
                <Target className="w-3 h-3 mr-1" /> Set Monthly Targets
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const v = variance.variance;
  const cal = variance.calendar;
  const ns = v.net_sales;
  const statusColor = { ON_TRACK: 'text-emerald-400 bg-emerald-500/10', AT_RISK: 'text-amber-400 bg-amber-500/10', BEHIND: 'text-red-400 bg-red-500/10' };
  const statusLabel = { ON_TRACK: 'On Track', AT_RISK: 'At Risk', BEHIND: 'Behind' };

  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid="target-variance-strip">
      <CardContent className="py-4 space-y-3">
        {/* Editor overlay */}
        {editing ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-300 font-medium">Edit Monthly Targets — {variance.month}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)} className="text-xs text-zinc-400"><X className="w-3 h-3 mr-1" />Cancel</Button>
                <Button size="sm" onClick={saveTargets} className="bg-amber-500 hover:bg-amber-600 text-black text-xs" data-testid="save-targets-btn"><Save className="w-3 h-3 mr-1" />Save</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-[10px] text-zinc-500">Net Sales</Label><Input type="number" value={editTargets.net_sales} onChange={e => setEditTargets(p => ({...p, net_sales: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="input-target-sales" /></div>
              <div><Label className="text-[10px] text-zinc-500">Food Cost %</Label><Input type="number" step="0.5" value={editTargets.food_cost_pct} onChange={e => setEditTargets(p => ({...p, food_cost_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="input-target-food" /></div>
              <div><Label className="text-[10px] text-zinc-500">Gross Margin %</Label><Input type="number" step="0.5" value={editTargets.gross_margin_pct} onChange={e => setEditTargets(p => ({...p, gross_margin_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="input-target-margin" /></div>
              <div><Label className="text-[10px] text-zinc-500">Labor Cost %</Label><Input type="number" step="0.5" value={editTargets.labor_cost_pct} onChange={e => setEditTargets(p => ({...p, labor_cost_pct: e.target.value}))} className="h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="input-target-labor" /></div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-zinc-300 font-medium">Monthly Target</span>
                <Badge className={`text-[10px] h-5 ${statusColor[variance.status]}`}>{statusLabel[variance.status]}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">Day {cal.days_elapsed}/{cal.days_in_month}</span>
                <button onClick={openEditor} className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300" data-testid="edit-targets-btn"><Pencil className="w-3 h-3" /></button>
              </div>
            </div>

            {/* Sales Target Progress */}
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-bold font-mono text-zinc-100" data-testid="actual-mtd-value">{formatCurrency(ns.actual_mtd)}</span>
                  <span className="text-xs text-zinc-500">/ {formatCurrency(ns.target)} target</span>
                </div>
                <span className={`text-sm font-mono font-semibold ${ns.gap_mtd >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="gap-mtd-value">
                  {ns.gap_mtd >= 0 ? '+' : ''}{formatCurrency(ns.gap_mtd)} vs expected
                </span>
              </div>

              {/* Expected MTD vs Actual — Time-aware gap */}
              <div className="flex items-center gap-2 py-1 px-2.5 rounded-md bg-zinc-800/60 border border-zinc-700/40" data-testid="expected-mtd-strip">
                <div className="flex items-center gap-1.5 flex-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${ns.gap_mtd >= 0 ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span className="text-[11px] text-zinc-400">
                    Expected by Day {cal.days_elapsed}:
                  </span>
                  <span className="text-[11px] font-mono font-medium text-zinc-300" data-testid="expected-mtd-amount">
                    {formatCurrency(ns.expected_mtd)}
                  </span>
                </div>
                <div className="text-right">
                  <span className={`text-[11px] font-mono font-semibold ${ns.gap_mtd >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {ns.gap_mtd >= 0 ? 'Ahead' : 'Behind'} by {formatCurrency(Math.abs(ns.gap_mtd))}
                  </span>
                </div>
              </div>

              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden relative">
                {/* Expected MTD marker */}
                {ns.target > 0 && (
                  <div className="absolute top-0 h-full w-0.5 bg-zinc-500 z-10" style={{ left: `${Math.min((ns.expected_mtd / ns.target) * 100, 100)}%` }} title={`Expected: ${formatCurrency(ns.expected_mtd)}`} />
                )}
                <div className={`h-full rounded-full transition-all ${ns.pct_achieved >= 95 ? 'bg-emerald-500' : ns.pct_achieved >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(ns.pct_achieved, 100)}%` }} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-zinc-500">
                <span>{ns.pct_achieved}% achieved</span>
                <span>Need {formatCurrency(ns.daily_required)}/day (actual: {formatCurrency(ns.daily_actual_rate)}/day)</span>
              </div>
            </div>

            {/* Metric Cards */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Food Cost', target: v.food_cost_pct?.target, actual: v.food_cost_pct?.actual, forecast: v.food_cost_pct?.forecast, gap: v.food_cost_pct?.gap, unit: '%', invert: true },
                { label: 'Gross Margin', target: v.gross_margin_pct?.target, actual: v.gross_margin_pct?.actual, forecast: v.gross_margin_pct?.forecast, gap: v.gross_margin_pct?.gap, unit: '%', invert: false },
                { label: 'Forecast Landing', target: ns.target, actual: ns.forecast_eom, forecast: ns.forecast_eom, gap: ns.forecast_gap, unit: 'currency', invert: false },
              ].map(m => {
                // Respect the truth contract: when the backend emits null
                // (actual suppressed upstream), never draw 0% — show em-dash.
                const actualSuppressed = m.actual === null || m.actual === undefined;
                const isGood = m.invert ? (m.gap || 0) <= 0 : (m.gap || 0) >= 0;
                const fmtValue = (v, u) => {
                  if (v === null || v === undefined) return '—';
                  return u === 'currency' ? formatCurrency(v) : `${v}%`;
                };
                return (
                  <div key={m.label} className="bg-zinc-800/40 rounded-lg p-2.5 space-y-1">
                    <p className="text-[10px] text-zinc-500">{m.label}</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm font-mono font-semibold text-zinc-200">
                        {fmtValue(m.actual, m.unit)}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        / {fmtValue(m.target, m.unit)}
                      </span>
                    </div>
                    {actualSuppressed ? (
                      <span className="text-[10px] text-zinc-500 italic">suppressed</span>
                    ) : (
                      m.gap !== null && m.gap !== undefined && (
                        <span className={`text-[10px] font-mono ${isGood ? 'text-emerald-400' : 'text-red-400'}`}>
                          {m.gap >= 0 ? '+' : ''}{m.unit === 'currency' ? formatCurrency(m.gap) : `${m.gap}%`}
                        </span>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const PnLPage = () => {
  const { user } = useAuth();
  const [pnlData, setPnlData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [scLedger, setScLedger] = useState(null);
  const [scCanonical, setScCanonical] = useState(null);
  
  // Shared Accounts period (URL-backed, default month-to-date in Asia/Kolkata).
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  const dateFrom = periodState.period.from;
  const dateTo = periodState.period.to;

  const fetchPnL = useCallback(async () => {
    try {
      setError(null);
      const [pnlRes, scRes] = await Promise.allSettled([
        accountsAPI.getRealtimePnL({
          ...periodState.apiParams,
          granularity: 'daily'
        }),
        accountsAPI.getServiceChargeSummary({
          date_from: dateFrom,
          date_to: dateTo,
        }),
      ]);
      if (pnlRes.status === 'fulfilled') setPnlData(pnlRes.value.data);
      else throw pnlRes.reason;
      if (scRes.status === 'fulfilled') setScLedger(scRes.value.data);
      // Canonical People allocation + Accounts settlement, for a single-month range only
      const monthScope = dateFrom?.slice(0, 7) === dateTo?.slice(0, 7) ? dateFrom.slice(0, 7) : null;
      if (monthScope) {
        try {
          const canon = await accountsAPI.getServiceChargeAllocationState(monthScope);
          setScCanonical(canon.data?.allocation?.synced ? canon.data : null);
        } catch (e) { setScCanonical(null); }
      } else { setScCanonical(null); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setError(err.response?.data?.detail || 'Failed to fetch P&L data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, periodState.apiParams]);

  useEffect(() => {
    setLoading(true);
    fetchPnL();
  }, [fetchPnL]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPnL();
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="pnl-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-12" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="pnl-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Real-Time P&L</h1>
          <p className="text-sm text-muted-foreground">
            Daily profit & loss statement with live data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <InlineIntegrityChip surface="pnl" />
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={pnlData} />
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-pnl-btn"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <PublishPnLDialog 
            dateFrom={dateFrom}
            dateTo={dateTo}
            pnlData={pnlData}
            onPublished={handleRefresh}
          />
        </div>
      </div>

      {error && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-400" />
            <p className="text-sm text-rose-400">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* P&L Truth Contract banner — gates the whole page. */}
      <PnLTruthBanner
        truthContract={pnlData?.truth_contract}
        cashMode={pnlData?.cashflow?.cash_mode}
        cashModeReason={pnlData?.cashflow?.cash_mode_reason}
      />

      {/* 7-day Truth Sparkline — at-a-glance trust surface. */}
      <TruthSparkline />

      {/* People Engine integration status — surfaces UNAVAILABLE/NOT_CONFIGURED
          payroll & service-charge streams so the user knows which obligations
          are genuinely unknown vs zero. Renders nothing on the happy path. */}
      <IntegrationStatusPanel />

      {/* Quick Actions Panel */}
      <QuickActionsPanel 
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSyncComplete={handleRefresh}
      />

      {/* Sales Source + Reconciliation Indicator */}
      {pnlData?.sales_source_meta && (() => {
        const srcRaw = pnlData.sales_source_meta.source || pnlData.sales?.source || 'NONE';
        // Normalize composite labels like "SC_TRANSACTIONS+SALES_CAPTURE"
        const isMirror = srcRaw.includes('SC_TRANSACTIONS');
        const isLive = srcRaw === 'SALES_CAPTURE';
        const isUpload = srcRaw === 'UPLOAD';
        const isPos = srcRaw === 'POS';
        const isNone = srcRaw === 'NONE' || !srcRaw;

        const recon = pnlData?.sales?.revenue_composition?.reconciliation || {};
        const reconciled = recon.reconciled === true;
        const reconTolerance = recon.tolerance;
        const reconDeltas = {
          core: recon.core_sales_delta,
          net: recon.net_sales_delta,
          collection: recon.collection_delta,
        };
        const salesReconMetric = pnlData?.truth_contract?.metrics?.sales_reconciliation || {};
        const reconInvalid = salesReconMetric.state === 'INVALID';

        // Container color: reconciliation state dominates, source is secondary
        const containerCls = reconInvalid
          ? 'bg-red-500/8 border-red-500/30'
          : isMirror
          ? 'bg-emerald-500/8 border-emerald-500/25'
          : isLive
          ? 'bg-blue-500/8 border-blue-500/25'
          : isUpload
          ? 'bg-amber-500/8 border-amber-500/25'
          : isPos
          ? 'bg-emerald-500/8 border-emerald-500/25'
          : 'bg-zinc-500/8 border-zinc-500/25';

        const labelCls = reconInvalid
          ? 'text-red-400'
          : isMirror
          ? 'text-emerald-400'
          : isLive
          ? 'text-blue-400'
          : isUpload
          ? 'text-amber-400'
          : isPos
          ? 'text-emerald-400'
          : 'text-zinc-400';

        // Human-friendly label for the source
        const humanSource = isMirror && srcRaw.includes('+SALES_CAPTURE')
          ? 'SC_TRANSACTIONS (mirror + live gap-fill)'
          : isMirror
          ? 'SC_TRANSACTIONS (canonical mirror)'
          : srcRaw;

        return (
          <div data-testid="sales-source-strip" className={`px-4 py-2.5 rounded-lg border ${containerCls}`}>
            <div className="flex items-center justify-between flex-wrap gap-y-1.5">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs font-medium ${labelCls}`} data-testid="pnl-sales-source">
                  Sales Source: {humanSource}
                </span>

                {/* Reconciliation badge — visible honesty check */}
                {!isNone && (
                  reconInvalid ? (
                    <span
                      data-testid="pnl-reconciliation-badge"
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-red-300 bg-red-500/15 border border-red-500/30 px-2 py-0.5 rounded-full"
                      title={salesReconMetric.reason || 'PNL_RECONCILIATION_FAILED'}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Reconciliation Failed
                    </span>
                  ) : reconciled ? (
                    <span
                      data-testid="pnl-reconciliation-badge"
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full"
                      title={`Tolerance ₹${reconTolerance?.toFixed?.(2) ?? reconTolerance} (0.1% of gross) · deltas: core ${reconDeltas.core ?? 0}, net ${reconDeltas.net ?? 0}, collection ${reconDeltas.collection ?? 0}`}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Reconciled
                    </span>
                  ) : null
                )}

                {isLive && (
                  <span className="text-[10px] text-zinc-500" data-testid="pnl-capture-meta">
                    {pnlData.sales_source_meta.total_orders} orders |
                    Confidence: {pnlData.sales_source_meta.confidence_summary?.avg_confidence?.toFixed(0) ?? '—'}%
                    {pnlData.sales_source_meta.non_actionable_pct > 0 && (
                      <span className="text-amber-400 ml-1">| {pnlData.sales_source_meta.non_actionable_pct}% non-actionable</span>
                    )}
                  </span>
                )}
                {isMirror && (
                  <span className="text-[10px] text-zinc-500" data-testid="pnl-mirror-meta">
                    {(pnlData.sales_source_meta.total_orders ?? pnlData.sales?.total_orders ?? 0)} orders · canonical truth
                  </span>
                )}
                {isUpload && pnlData.sales_source_meta.upload_fallback && (
                  <span className="text-[10px] text-zinc-500">
                    ({pnlData.sales_source_meta.upload_fallback.file_name} |
                    Mapping: {((pnlData.sales_source_meta.upload_fallback.mapping_confidence || 0) * 100).toFixed(0)}% |
                    Penalty: -{pnlData.sales_source_meta.confidence_penalty}pts)
                  </span>
                )}
                {isPos && (
                  <span className="text-[10px] text-zinc-500">Live POS sync active</span>
                )}
              </div>
              <a href="/sales-capture" className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors">
                <Eye className="w-3 h-3" /> Sales Source
              </a>
            </div>

            {/* Reconciliation Failure Banner — blocks interpretation */}
            {reconInvalid && (
              <div
                data-testid="pnl-reconciliation-failed-banner"
                className="mt-2 pt-2 border-t border-red-500/20 text-[11px] text-red-200"
              >
                <div className="font-medium mb-0.5">Data inconsistent — not decision-grade.</div>
                <div className="text-red-300/80">
                  Sales, category splits, and collections do not reconcile to the same truth source.
                  {typeof reconDeltas.core === 'number' && (
                    <span className="ml-1 text-red-300/60">
                      Deltas — core: ₹{reconDeltas.core?.toFixed?.(2) ?? reconDeltas.core},
                      net: ₹{reconDeltas.net?.toFixed?.(2) ?? reconDeltas.net},
                      collection: ₹{reconDeltas.collection?.toFixed?.(2) ?? reconDeltas.collection}
                      · tolerance ₹{reconTolerance?.toFixed?.(2) ?? reconTolerance}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Reconciliation Panel — visible honesty check of the sales bridges */}
      {pnlData?.sales?.revenue_composition && (() => {
        const s = pnlData.sales || {};
        const rc = s.revenue_composition || {};
        const recon = rc.reconciliation || {};
        if (recon.reconciled === undefined) return null;
        const isValid = recon.reconciled === true;
        const tol = recon.tolerance;
        const gross = s.gross_sales ?? 0;
        const disc = s.discounts ?? 0;
        const core = rc.core_sales ?? 0;
        const pkgTotal = rc.packaging_charges?.total ?? 0;
        const svc = rc.service_charge?.collected ?? 0;
        const tax = s.total_tax ?? 0;
        const net = s.net_sales ?? 0;
        const cat = (s.food_sales ?? 0) + (s.alcohol_sales ?? 0) + (s.beverage_sales ?? 0);
        const coll = rc.total_customer_collection ?? 0;
        const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const bridges = [
          { label: 'Core Sales', lhs: core, rhsLabel: 'Gross − Discounts', rhs: gross - disc, delta: core - (gross - disc) },
          { label: 'Category Sales', lhs: cat, rhsLabel: 'Core Sales', rhs: core, delta: cat - core },
          { label: 'Net Sales', lhs: net, rhsLabel: 'Core + Packaging', rhs: core + pkgTotal, delta: net - (core + pkgTotal) },
          { label: 'Customer Collection', lhs: coll, rhsLabel: 'Net + Service + Tax', rhs: net + svc + tax, delta: coll - (net + svc + tax) },
        ];
        return (
          <details
            data-testid="pnl-reconciliation-panel"
            className={`rounded-lg border ${isValid ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/25'}`}
          >
            <summary className="px-4 py-2.5 cursor-pointer text-xs flex items-center justify-between select-none">
              <span className="flex items-center gap-2">
                {isValid ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                )}
                <span className={isValid ? 'text-emerald-300 font-medium' : 'text-red-300 font-medium'}>
                  Reconciliation {isValid ? 'Passed' : 'Failed'}
                </span>
                <span className="text-zinc-500">
                  · {bridges.filter(b => Math.abs(b.delta) <= (tol ?? 1)).length}/{bridges.length} bridges within tolerance ₹{Number(tol ?? 1).toFixed(2)}
                </span>
              </span>
              <span className="text-[10px] text-zinc-500">click to inspect</span>
            </summary>
            <div className="px-4 pb-3 pt-1 text-[11px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                {bridges.map((b) => {
                  const ok = Math.abs(b.delta) <= (tol ?? 1);
                  return (
                    <div
                      key={b.label}
                      data-testid={`pnl-bridge-${b.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className="flex items-center justify-between gap-3 py-1 border-b border-zinc-800/40 last:border-b-0"
                    >
                      <span className="text-zinc-400 min-w-[8rem]">{b.label}</span>
                      <span className="font-mono text-zinc-200">{fmt(b.lhs)}</span>
                      <span className="text-zinc-600">==</span>
                      <span className="text-zinc-500 text-right min-w-[9rem]">{b.rhsLabel}</span>
                      <span className="font-mono text-zinc-200">{fmt(b.rhs)}</span>
                      <span className={`font-mono min-w-[6rem] text-right ${ok ? 'text-emerald-400' : 'text-red-400'}`}>
                        Δ {b.delta >= 0 ? '+' : ''}{Number(b.delta).toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        );
      })()}


      {/* Confidence / Validation Gating Warning */}
      {pnlData?.capture_confidence_warning && pnlData.capture_confidence_warning.message && (
        <div data-testid="confidence-gating-warning" className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
          pnlData.capture_confidence_warning.has_failures 
            ? 'bg-red-500/8 border-red-500/30' 
            : 'bg-amber-500/8 border-amber-500/30'
        }`}>
          <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
            pnlData.capture_confidence_warning.has_failures ? 'text-red-400' : 'text-amber-400'
          }`} />
          <div className="text-xs leading-relaxed">
            <p className={`font-medium mb-0.5 ${pnlData.capture_confidence_warning.has_failures ? 'text-red-300' : 'text-amber-300'}`}>
              Data Quality Warning
            </p>
            <p className="text-zinc-400">{pnlData.capture_confidence_warning.message}</p>
            <div className="flex items-center gap-3 mt-1.5 text-[10px]">
              <span className="text-zinc-500">Avg confidence: <span className={
                pnlData.capture_confidence_warning.avg_confidence >= 70 ? 'text-emerald-400' : 'text-red-400'
              }>{pnlData.capture_confidence_warning.avg_confidence?.toFixed(0)}%</span></span>
              {pnlData.capture_confidence_warning.low_confidence_pct > 0 && (
                <span className="text-amber-400">{pnlData.capture_confidence_warning.low_confidence_pct}% low confidence</span>
              )}
              {pnlData.capture_confidence_warning.fail_pct > 0 && (
                <span className="text-red-400">{pnlData.capture_confidence_warning.fail_pct}% validation failures</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cash Position Strip (from Expense Engine) — arithmetic contract */}
      {pnlData?.expense_engine?.available && pnlData.expense_engine.cash_position && (() => {
        const cp = pnlData.expense_engine.cash_position;
        const hasLegacyDifference = cp.legacy_observation_status === 'DIFFERENCE_PRESENT';
        const truthState = pnlData?.truth_contract?.state;
        const isTruthSuppressed = truthState && truthState !== 'VALID';
        // Cash arithmetic contract: if opening is UNKNOWN, we MUST NOT render
        // a closing figure — we show "Net Cash Movement" instead. Backend
        // signals this via cash_mode === 'PARTIAL' with nulled opening/closing.
        const isPartial = cp.cash_mode === 'PARTIAL' || cp.opening_cash === null;
        const netMovement = cp.net_cash_movement ?? ((cp.cash_inflows || 0) - (cp.cash_outflows || 0));
        return (
          <Card
            className={`bg-card/50 backdrop-blur-sm border-border/50 ${
              isTruthSuppressed ? 'opacity-70' : ''
            }`}
            data-testid="cash-position-strip"
          >
            <CardContent className="p-3">
              {isTruthSuppressed && (
                <div
                  className={`mb-2 flex items-center gap-2 px-2 py-1 rounded text-[10px] border ${
                    truthState === 'INVALID'
                      ? 'border-rose-500/30 bg-rose-500/10 text-rose-300'
                      : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                  }`}
                  data-testid="cash-position-truth-gate"
                >
                  <AlertTriangle className="w-3 h-3" />
                  <span>
                    {truthState === 'INVALID'
                      ? 'Directional only — P&L truth invalid. Treat cash as reference, not decision-grade.'
                      : 'Directional only — cost layers suppressed. Cash shown for reference.'}
                  </span>
                </div>
              )}
              {isPartial && (
                <div
                  className="mb-2 flex items-center gap-2 px-2 py-1 rounded text-[10px] border border-amber-500/30 bg-amber-500/10 text-amber-300"
                  data-testid="cash-position-partial-banner"
                >
                  <Info className="w-3 h-3" />
                  <span>
                    Opening balance not configured — showing <span className="font-semibold">Net Cash Movement</span> only.
                    Full cash position requires opening balances in Settings.
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                  {isPartial ? (
                    // PARTIAL — inflows − outflows = Net Movement only
                    <>
                      <div>
                        <p className="text-[9px] text-emerald-400/60 uppercase tracking-wider">Inflows</p>
                        <p className="text-sm font-mono text-emerald-400" data-testid="cash-inflows">
                          +{formatCurrency(cp.cash_inflows)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] text-red-400/60 uppercase tracking-wider">Outflows</p>
                        <p className="text-sm font-mono text-red-400" data-testid="cash-outflows">
                          -{formatCurrency(cp.cash_outflows || 0)}
                        </p>
                      </div>
                      <span className="text-muted-foreground">=</span>
                      <div className="px-3 py-1.5 bg-amber-500/10 rounded-lg border border-amber-500/30">
                        <p className="text-[9px] text-amber-400 uppercase tracking-wider">Net Cash Movement</p>
                        <p
                          className={`text-lg font-mono font-bold ${netMovement >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                          data-testid="cash-net-movement"
                        >
                          {netMovement >= 0 ? '+' : ''}{formatCurrency(netMovement)}
                        </p>
                      </div>
                      <div
                        className="px-2 py-1 bg-zinc-500/10 rounded border border-zinc-500/25"
                        data-testid="cash-opening-unknown"
                        title="Opening balance not configured. Enable Cash Closing in Settings."
                      >
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider leading-none">Opening</p>
                        <p className="text-[12px] text-zinc-300 font-mono leading-none mt-0.5">—</p>
                      </div>
                    </>
                  ) : (
                    // FULL — opening + inflows − outflows = closing
                    <>
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4 text-emerald-400 opacity-60" />
                        <div>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Opening</p>
                          <p className="text-sm font-mono">{formatCurrency(cp.opening_cash)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[9px] text-emerald-400/60 uppercase tracking-wider">Inflows</p>
                        <p className="text-sm font-mono text-emerald-400">+{formatCurrency(cp.cash_inflows)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-red-400/60 uppercase tracking-wider">Cash Burn</p>
                        <p className="text-sm font-mono text-red-400">-{formatCurrency(cp.daily_cash_burn)}</p>
                      </div>
                      <span className="text-muted-foreground">=</span>
                      <div className="px-3 py-1.5 bg-zinc-500/10 rounded-lg border border-zinc-500/30" data-testid="pnl-legacy-ec-closing">
                        <p className="text-[9px] text-zinc-400 uppercase tracking-wider">Legacy EC Closing</p>
                        <p className="text-lg font-mono font-bold text-zinc-200">{formatCurrency(cp.reported_closing_cash)}</p>
                        <p className="text-[8px] text-zinc-500 mt-0.5">EC_LEGACY_OBSERVATION — not cash truth</p>
                      </div>
                      {hasLegacyDifference && (
                        <div className="px-3 py-1.5 bg-zinc-500/10 rounded-lg border border-zinc-500/30" data-testid="pnl-legacy-difference">
                          <p className="text-[9px] text-zinc-400 uppercase tracking-wider">Accounts Computed</p>
                          <p className="text-sm font-mono text-zinc-200">{formatCurrency(cp.computed_closing_cash)}</p>
                          <p className="text-[8px] text-zinc-500 font-medium mt-0.5">Difference: {formatCurrency(cp.legacy_observation_difference)} — not a reconciliation</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {pnlData.expense_engine.classification && (
                    <div className="text-right">
                      <p className="text-[9px] text-muted-foreground">Emergency Procurement</p>
                      <p className={`text-xs font-mono ${(pnlData.expense_engine.classification.emergency_procurement_ratio || 0) > 0.3 ? 'text-red-400' : 'text-zinc-300'}`}>
                        {((pnlData.expense_engine.classification.emergency_procurement_ratio || 0) * 100).toFixed(1)}%
                      </p>
                    </div>
                  )}
                  <a href="/cash" className="text-[10px] text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
                    Full Dashboard
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Today's Story Panel - Driver-powered narrative */}
      <TodaysStoryPanel dateFrom={dateFrom} dateTo={dateTo} />

      {pnlData && (
        <>
          {/* ══════════ REVENUE BRIDGES (replaces the old Gross−Tax=Net strip) ══════════
              Two explicit bridges + a category breakdown, each reconciled on
              the backend. If any delta exceeds tolerance, a mismatch warning
              is rendered instead of silently reconciled math.
          */}
          {(() => {
            const s = pnlData.sales || {};
            const rc = s.revenue_composition || {};
            const pkg = rc.packaging_charges || {};
            const svc = rc.service_charge || {};
            const recon = rc.reconciliation || {};
            const mismatched = recon && recon.reconciled === false;
            const meta = pnlData.sales_source_meta || {};
            const mc = meta.mapping_completeness;
            const catTotal = (s.food_sales || 0) + (s.alcohol_sales || 0) + (s.beverage_sales || 0);

            // Shared pieces of a bridge row
            const BridgeLine = ({ label, value, op, color, testId, sub }) => (
              <div className="flex items-center gap-3">
                {op && <span className="text-zinc-500 w-4 text-center font-mono">{op}</span>}
                <div className="flex-1">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
                  {sub && <p className="text-[9px] text-zinc-600 leading-tight mt-0.5">{sub}</p>}
                </div>
                <p className={`text-sm font-mono font-medium ${color}`} data-testid={testId}>
                  {formatCurrency(value)}
                </p>
              </div>
            );

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="revenue-bridges">
                {/* ─── Revenue Bridge ─── */}
                <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="revenue-bridge-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-400 uppercase tracking-wider font-medium">Revenue Bridge</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">Gross → Core → Net (business revenue)</p>
                      </div>
                      {mc && (
                        <div className="flex items-center gap-1.5 text-[10px]" data-testid="mapping-completeness-strip">
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            mc.completeness_pct >= 100 ? 'bg-emerald-400' :
                            mc.completeness_pct >= 90 ? 'bg-blue-400' : 'bg-amber-400'
                          }`} />
                          <span className={`font-mono font-semibold ${
                            mc.completeness_pct >= 100 ? 'text-emerald-400' :
                            mc.completeness_pct >= 90 ? 'text-blue-400' : 'text-amber-400'
                          }`}>
                            {mc.completeness_pct}% mapped
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <BridgeLine label="Gross Sales" value={s.gross_sales} color="text-zinc-200" testId="rb-gross-sales" />
                      <BridgeLine op="−" label="Discounts" value={s.discounts ?? 0} color="text-rose-400" testId="rb-discounts"
                        sub={!s.discounts || s.discounts === 0 ? 'No discounts this period' : undefined} />
                      <div className="border-t border-zinc-800/60 pt-1.5">
                        <BridgeLine label="Core Sales" value={rc.core_sales} color="text-emerald-400" testId="rb-core-sales" />
                      </div>
                      <BridgeLine op="+" label="Packaging Charges" value={pkg.total ?? 0} color="text-blue-400" testId="rb-packaging"
                        sub={(pkg.zomato > 0 || pkg.swiggy > 0)
                          ? `Zomato ${formatCurrency(pkg.zomato || 0)} · Swiggy ${formatCurrency(pkg.swiggy || 0)}`
                          : undefined} />
                      <div className="border-t border-emerald-500/30 pt-2 mt-1">
                        <div className="flex items-center gap-3 px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                          <span className="text-zinc-500 w-4 text-center font-mono">=</span>
                          <div className="flex-1">
                            <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">Net Sales</p>
                            <p className="text-[9px] text-zinc-500 mt-0.5">Business revenue used for P&amp;L</p>
                          </div>
                          <p className="text-xl font-mono font-bold text-emerald-400" data-testid="net-sales-value">
                            {formatCurrency(s.net_sales)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {mismatched && (recon.core_sales_delta !== 0 || recon.net_sales_delta !== 0) && (
                      <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[11px]"
                           data-testid="revenue-bridge-mismatch">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                        <div>
                          <div className="font-medium">Revenue bridge mismatch</div>
                          <div className="opacity-80 mt-0.5">
                            Core Sales off by {formatCurrency(recon.core_sales_delta)}
                            {recon.net_sales_delta !== 0 && <> · Net Sales off by {formatCurrency(recon.net_sales_delta)}</>}
                            . Check discounts or charge mapping.
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* ─── Customer Collection Bridge ─── */}
                <Card className="bg-card/50 backdrop-blur-sm border-border/50" data-testid="collection-bridge-card">
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <p className="text-xs text-zinc-300 uppercase tracking-wider font-medium">Customer Collection</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">Net Sales → Charges → Total collected from customer</p>
                    </div>

                    <div className="space-y-1.5">
                      <BridgeLine label="Net Sales" value={s.net_sales} color="text-emerald-400" testId="cc-net-sales" />
                      <BridgeLine op="+" label="Service Charge" value={svc.collected ?? 0} color="text-amber-400" testId="cc-service-charge"
                        sub="Liability — held for team" />
                      <BridgeLine op="+" label="GST/VAT" value={s.total_tax} color="text-amber-400" testId="cc-tax"
                        sub="Liability — payable" />
                      <div className="border-t border-zinc-700/60 pt-2 mt-1">
                        <div className="flex items-center gap-3 px-3 py-2 bg-zinc-800/60 rounded-lg border border-zinc-700/50">
                          <span className="text-zinc-500 w-4 text-center font-mono">=</span>
                          <div className="flex-1">
                            <p className="text-[10px] text-zinc-200 uppercase tracking-wider font-semibold">Total Customer Collection</p>
                            <p className="text-[9px] text-zinc-500 mt-0.5">Cash collected from customers (not revenue)</p>
                          </div>
                          <p className="text-xl font-mono font-bold text-zinc-100" data-testid="customer-collection-value">
                            {formatCurrency(rc.total_customer_collection)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {mismatched && recon.collection_delta !== 0 && (
                      <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[11px]"
                           data-testid="collection-bridge-mismatch">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                        <div>
                          <div className="font-medium">Collection bridge mismatch</div>
                          <div className="opacity-80 mt-0.5">
                            Off by {formatCurrency(recon.collection_delta)}. Check taxes or service-charge mapping.
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* ─── Category Sales — Core Sales Only ─── */}
                <Card className="bg-card/50 backdrop-blur-sm border-border/50 lg:col-span-2" data-testid="category-sales-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-zinc-300 uppercase tracking-wider font-medium">Category Sales</p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">Core Sales only — reconciles to {formatCurrency(rc.core_sales)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</p>
                        <p className="text-sm font-mono font-semibold text-zinc-200" data-testid="category-total">
                          {formatCurrency(catTotal)}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="px-3 py-2 bg-zinc-800/40 rounded-lg" data-testid="cat-food">
                        <p className="text-[10px] text-zinc-500">Food</p>
                        <p className="text-sm font-mono font-medium text-zinc-200">{formatCurrency(s.food_sales)}</p>
                      </div>
                      <div className="px-3 py-2 bg-zinc-800/40 rounded-lg" data-testid="cat-alcohol">
                        <p className="text-[10px] text-zinc-500">Alcohol</p>
                        <p className="text-sm font-mono font-medium text-zinc-200">{formatCurrency(s.alcohol_sales)}</p>
                      </div>
                      <div className="px-3 py-2 bg-zinc-800/40 rounded-lg" data-testid="cat-beverage">
                        <p className="text-[10px] text-zinc-500">Beverage</p>
                        <p className="text-sm font-mono font-medium text-zinc-200">{formatCurrency(s.beverage_sales)}</p>
                      </div>
                    </div>
                    {Math.abs(catTotal - (rc.core_sales || 0)) > 1.0 && (
                      <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 text-[11px]"
                           data-testid="category-mismatch">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                        <div>
                          Category splits sum to {formatCurrency(catTotal)} but Core Sales is {formatCurrency(rc.core_sales)}.
                          Off by {formatCurrency(catTotal - (rc.core_sales || 0))}.
                        </div>
                      </div>
                    )}
                    {mc && mc.pending_items > 0 && (
                      <p className="text-[10px] text-amber-400/80 mt-2">
                        {mc.pending_items} items pending mapping worth {formatCurrency(mc.pending_revenue)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Target vs Actual vs Forecast */}
          <TargetVarianceStrip dateFrom={dateFrom} dateTo={dateTo} />

          {/* KPI Cards */}
          {pnlData.sales?.net_sales === 0 && pnlData.cogs?.total_cogs === 0 && (pnlData.profit?.net_profit === 0 || !pnlData.profit?.net_profit) && (
            <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4 flex items-center gap-3" data-testid="pnl-no-data-banner">
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm text-zinc-300 font-medium">No sales or expense data found for this date range</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Selected: {dateFrom} to {dateTo}. Try selecting a date range with uploaded sales data.
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {(() => {
              // Truth-gated KPI rendering — the page's single rule is:
              // never render a value for a metric whose truth state is not
              // VALID/DIRECTIONAL. The banner already explained why; the
              // tiles must not contradict it.
              const tcMetrics = pnlData?.truth_contract?.metrics || {};
              const isRenderable = (name) => {
                const m = tcMetrics[name];
                if (!m) return true; // metric not tracked — trust flat field
                return m.state === 'VALID' || m.state === 'DIRECTIONAL';
              };
              const cogsOK = isRenderable('total_cogs');
              const gpOK = isRenderable('gross_profit');
              const gmOK = isRenderable('gross_margin');
              const npOK = isRenderable('net_profit') && gpOK;
              const gmPct = pnlData.profit?.gross_margin_pct;
              return (
                <>
                  <KPICard
                    title="Net Sales"
                    value={pnlData.sales?.net_sales}
                    icon={DollarSign}
                    variant="primary"
                    testId="kpi-net-sales"
                  />
                  <KPICard
                    title={cogsOK ? 'Total COGS' : 'Total COGS (Purchase Proxy)'}
                    value={cogsOK ? pnlData.cogs?.total_cogs : '—'}
                    subtitle={cogsOK ? undefined : 'Not decision-grade'}
                    icon={Package}
                    variant={cogsOK ? 'warning' : 'default'}
                    testId="kpi-total-cogs"
                  />
                  <KPICard
                    title="Gross Profit"
                    value={gpOK ? pnlData.profit?.gross_profit : '—'}
                    subtitle={gpOK ? undefined : 'Suppressed — COGS invalid'}
                    icon={TrendingUp}
                    variant={gpOK && pnlData.profit?.gross_profit >= 0 ? 'success' : gpOK ? 'danger' : 'default'}
                    testId="kpi-gross-profit"
                  />
                  <KPICard
                    title="Gross Margin"
                    value={gmOK ? formatPercent(gmPct) : '—'}
                    subtitle={gmOK
                      ? (gmPct >= 60 ? 'Healthy' : 'Below target')
                      : 'Suppressed — COGS invalid'}
                    variant={gmOK ? (gmPct >= 60 ? 'success' : 'warning') : 'default'}
                    testId="kpi-gross-margin"
                  />
                  {(() => {
                    const opexTruth = pnlData.opex?.truth?.state;
                    const opexKnown = opexTruth === 'AVAILABLE' || opexTruth === 'AUTHORITATIVE_ZERO';
                    const opexZero = (pnlData.opex?.total_opex || 0) === 0;
                    return (
                      <KPICard
                        title="Total Opex"
                        value={opexKnown ? (pnlData.opex?.total_opex || 0) : '—'}
                        subtitle={!opexKnown
                          ? 'No synced OPEX data'
                          : (opexZero ? 'Nothing expensed this period' : undefined)}
                        icon={Flame}
                        variant={opexKnown && !opexZero ? 'info' : 'default'}
                        testId="kpi-total-opex"
                      />
                    );
                  })()}
                  <KPICard
                    title="Net Profit"
                    value={npOK ? pnlData.profit?.net_profit : '—'}
                    subtitle={npOK ? undefined : 'Suppressed — depends on valid COGS'}
                    icon={npOK && pnlData.profit?.net_profit >= 0 ? TrendingUp : TrendingDown}
                    variant={npOK ? (pnlData.profit?.net_profit >= 0 ? 'success' : 'danger') : 'default'}
                    testId="kpi-net-profit"
                  />
                </>
              );
            })()}
          </div>

          {/* Month Forecast — Landing Engine */}
          <ForecastCard
            dateFrom={dateFrom}
            dateTo={dateTo}
            truthContract={pnlData?.truth_contract}
          />

          {/* Cost Truth Banner — brief Part 1/9 visible state */}
          {pnlData.cogs_truth && (
            <CostTruthBanner cogsTruth={pnlData.cogs_truth} />
          )}

          {/* Confidence Strip */}
          <ConfidenceStrip 
            confidence={pnlData.confidence} 
            sourcesLastSync={pnlData.sources_last_sync}
            cogsMeta={pnlData.cogs_meta}
            duplicateRisk={pnlData.duplicate_risk}
          />

          {/* Sanity Checks Panel */}
          {pnlData.sanity_checks && pnlData.sanity_checks.length > 0 && (
            <SanityChecksPanel 
              checks={pnlData.sanity_checks}
              cogsMeta={pnlData.cogs_meta}
            />
          )}

          {/* Margin Alerts Panel */}
          <MarginAlertsPanel dateFrom={dateFrom} dateTo={dateTo} />

          {/* Operating Pulse Preview */}
          <PulsePreviewPanel dateFrom={dateFrom} dateTo={dateTo} />

          {/* EOD Close Pulse Preview */}
          <EodClosePulsePanel dateFrom={dateFrom} />

          {/* Expandable COGS Blocks */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                {pnlData.cogs_truth?.actionability_stance === 'NOT_ACTIONABLE'
                  ? 'Cost of Goods (Purchase Proxy — not decision-grade)'
                  : 'Cost of Goods Sold (COGS)'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Accordion type="multiple" className="w-full">
                <COGSDetailBlock 
                  title="Food Cost"
                  icon={Utensils}
                  data={pnlData.cogs?.food}
                  salesAmount={pnlData.sales?.food_sales}
                  color="emerald"
                />
                <COGSDetailBlock 
                  title="Liquor Cost"
                  icon={Wine}
                  data={pnlData.cogs?.liquor}
                  salesAmount={pnlData.sales?.alcohol_sales}
                  color="violet"
                />
                <COGSDetailBlock 
                  title="Beverage Cost"
                  icon={Coffee}
                  data={pnlData.cogs?.beverage}
                  salesAmount={pnlData.sales?.beverage_sales}
                  color="blue"
                />
              </Accordion>
            </CardContent>
          </Card>

          {/* Operating Expenses Block */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Operating Expenses</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Accordion type="multiple" className="w-full">
                <AccordionItem value="utility" className="border-border/30">
                  <AccordionTrigger className="hover:no-underline py-3 px-4">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                          <Flame className="w-4 h-4" />
                        </div>
                        <span className="font-medium">Utility</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-lg font-semibold">
                          {formatCurrency(pnlData.opex?.utility?.total)}
                        </span>
                        <Badge variant="secondary">
                          {formatPercent(pnlData.opex?.utility?.pct_of_food_sales)} of food
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Kitchen Gas</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.utility?.kitchen_gas)}</p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Kitchen Tools</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.utility?.kitchen_tools)}</p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Total</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.utility?.total)}</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="packing" className="border-border/30">
                  <AccordionTrigger className="hover:no-underline py-3 px-4">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                          <Box className="w-4 h-4" />
                        </div>
                        <span className="font-medium">Packing Material</span>
                      </div>
                      <span className="font-mono text-lg font-semibold">
                        {formatCurrency(pnlData.opex?.packing?.total)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Bar</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.packing?.bar)}</p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Kitchen</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.packing?.kitchen)}</p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Total</p>
                        <p className="font-mono font-medium">{formatCurrency(pnlData.opex?.packing?.total)}</p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="staff" className="border-border/30">
                  <AccordionTrigger className="hover:no-underline py-3 px-4" data-testid="pnl-staff-cost-trigger">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                          <Users className="w-4 h-4" />
                        </div>
                        <span className="font-medium">Payroll / Staff Costs</span>
                      </div>
                      <span className="font-mono text-lg font-semibold" data-testid="pnl-staff-cost-total">
                        {formatCurrency(pnlData.opex?.staff_costs?.total)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Recognised payroll</p>
                        <p className="font-mono font-medium" data-testid="pnl-staff-cost-recognised">
                          {formatCurrency(pnlData.opex?.staff_costs?.total)}
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">% of sales</p>
                        <p className="font-mono font-medium">
                          {formatPercent(pnlData.opex?.staff_costs?.pct_of_sales)}
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Payroll cycles</p>
                        <p className="font-mono font-medium" data-testid="pnl-staff-cost-cycles">
                          {(pnlData.opex?.staff_costs?.cycles || []).length || 0}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3" data-testid="pnl-staff-cost-source">
                      From payroll recognised in Accounts against People's locked payroll.
                      Service charge is not included, and older payroll cash rows are not counted here.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sc-payouts" className="border-border/30">
                  <AccordionTrigger className="hover:no-underline py-3 px-4" data-testid="pnl-sc-payout-trigger">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                          <Wallet className="w-4 h-4" />
                        </div>
                        <span className="font-medium">Service Charge Payouts</span>
                      </div>
                      <span className="font-mono text-lg font-semibold" data-testid="pnl-sc-payout-total">
                        {formatCurrency(pnlData.opex?.service_charge_payouts?.total)}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Paid out this period</p>
                        <p className="font-mono font-medium" data-testid="pnl-sc-payout-gross">
                          {formatCurrency(pnlData.opex?.service_charge_payouts?.gross_payouts)}
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Reversed this period</p>
                        <p className="font-mono font-medium text-rose-400" data-testid="pnl-sc-reversal-total">
                          {(pnlData.opex?.service_charge_payouts?.reversals_total || 0) > 0 ? '−' : ''}
                          {formatCurrency(pnlData.opex?.service_charge_payouts?.reversals_total)}
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg">
                        <p className="text-muted-foreground text-xs mb-1">Net expense</p>
                        <p className="font-mono font-medium" data-testid="pnl-sc-payout-net">
                          {formatCurrency(pnlData.opex?.service_charge_payouts?.total)}
                        </p>
                      </div>
                    </div>
                    {(pnlData.opex?.service_charge_payouts?.payouts || []).length > 0 && (
                      <div className="mt-3 space-y-1.5" data-testid="pnl-sc-payout-lines">
                        {pnlData.opex.service_charge_payouts.payouts.map((p) => (
                          <div
                            key={p.payout_id}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-card/50 text-xs"
                          >
                            <span className="truncate">
                              {p.recipient_name || p.employee_id}
                              <span className="text-muted-foreground"> · {p.payout_date} · {p.payment_method}</span>
                            </span>
                            <span className="font-mono shrink-0">{formatCurrency(Number(p.amount))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(pnlData.opex?.service_charge_payouts?.reversals || []).length > 0 && (
                      <div className="mt-2 space-y-1.5" data-testid="pnl-sc-reversal-lines">
                        {pnlData.opex.service_charge_payouts.reversals.map((r) => (
                          <div
                            key={r.reversal_id}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-rose-500/5 border border-rose-500/20 text-xs"
                          >
                            <span className="truncate text-rose-200">
                              Reversed · {r.recipient_name || r.employee_id}
                              <span className="text-muted-foreground"> · {r.reversal_date} · {r.reason}</span>
                            </span>
                            <span className="font-mono shrink-0 text-rose-300">−{formatCurrency(Number(r.amount))}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-3" data-testid="pnl-sc-payout-source">
                      Only service charge actually paid out to the team is an expense, booked on the
                      payout date{(pnlData.opex?.service_charge_payouts?.allocation_periods || []).length > 0
                        ? ` (allocated for ${pnlData.opex.service_charge_payouts.allocation_periods.join(', ')})`
                        : ''} and credited back on the date it is reversed. Outstanding allocation
                      stays a liability and is not in this total. Kept separate from Payroll / Staff Costs.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* ─── Service Charge Ledger (Liability Tracker) v2 ─── */}
          {scLedger && scLedger.collected?.total > 0 && (() => {
            const policyStatus = scLedger.policy?.status;
            const policyActive = policyStatus === 'ACTIVE';
            const distPct = scLedger.policy?.distribution_pct;
            const retPct = scLedger.policy?.retention_pct;
            const distributablePool = scLedger.computed?.distributable_pool;
            const retainedAmount = scLedger.computed?.retained_amount;
            const finesDeductions = scLedger.policy?.fines_deductions_total;
            const nextPayout = scLedger.payout?.next_payout_date;
            const daysToPayout = scLedger.payout?.days_to_payout;
            const risk = scCanonical
              ? ({ SETTLED: 'FULLY_DISTRIBUTED', PARTIALLY_SETTLED: 'PARTIALLY_DISTRIBUTED',
                   OUTSTANDING: 'UNDISTRIBUTED' }[scCanonical.settlement?.state]
                 || scCanonical.settlement?.state)
              : (scLedger.risk_status || scLedger.ledger_status);
            const pendingValue = scLedger.ledger?.pending ?? scLedger.pending_balance;
            const ledgerFormula = scLedger.ledger?.formula || 'collected - paid (legacy)';

            // Risk badge color map — POLICY_NOT_CONNECTED is rose, OVERDUE
            // is red, RELEASE_DUE is amber, FULLY_DISTRIBUTED is emerald.
            const riskBadge =
              risk === 'POLICY_NOT_CONNECTED' ? 'bg-rose-500/10 text-rose-300' :
              risk === 'OVERDUE' ? 'bg-red-500/10 text-red-400' :
              risk === 'RELEASE_DUE' ? 'bg-amber-500/15 text-amber-300' :
              risk === 'FULLY_DISTRIBUTED' ? 'bg-emerald-500/10 text-emerald-400' :
              risk === 'PARTIALLY_DISTRIBUTED' ? 'bg-blue-500/10 text-blue-400' :
              'bg-amber-500/10 text-amber-400';

            return (
            <Card className="bg-card/50 backdrop-blur border-amber-500/20" data-testid="service-charge-ledger">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
                      <Wallet className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">Service Charge Ledger</h3>
                      <p className="text-[10px] text-amber-500/60">Liability — Not Revenue</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-medium",
                        policyActive ? 'bg-emerald-500/10 text-emerald-400' :
                        policyStatus === 'NOT_CONFIGURED' ? 'bg-amber-500/10 text-amber-300' :
                        'bg-rose-500/10 text-rose-300'
                      )}
                      data-testid="sc-policy-status"
                      title={scLedger.policy?.reason || ''}
                    >
                      Policy: {policyStatus?.replace(/_/g, ' ') || '—'}
                    </span>
                    <span className={cn(
                      "text-[10px] px-2 py-0.5 rounded-full font-medium",
                      riskBadge
                    )} data-testid="sc-ledger-status">
                      {risk?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                {/* Policy-not-connected explainer — shown only when policy
                    is missing/unavailable, never when ACTIVE. */}
                {!policyActive && (
                  <div
                    className="flex items-start gap-2 px-3 py-2 rounded-md border border-rose-500/25 bg-rose-500/5 text-rose-200 text-[11px]"
                    data-testid="sc-policy-not-connected"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-rose-400" />
                    <div>
                      <div className="font-medium">Distribution policy not connected</div>
                      <div className="opacity-80 mt-0.5">
                        People Engine is not exposing a service-charge policy yet.
                        Distributable pool, retained amount, and payout schedule are hidden until policy is configured.
                        Pending uses the legacy <span className="font-mono">collected − paid</span> formula.
                      </div>
                    </div>
                  </div>
                )}

                {/* Ledger Flow — 7 tiles when policy active, 4 tiles otherwise */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-collected">
                    <p className="text-[10px] text-zinc-500">Collected</p>
                    <p className="text-sm font-mono font-medium text-amber-400">
                      {formatCurrency(scLedger.collected?.total)}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">{scLedger.collected?.txn_count} dine-in txns</p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-distributable">
                    <p className="text-[10px] text-zinc-500">Distributable Pool</p>
                    <p className="text-sm font-mono font-medium text-emerald-400">
                      {policyActive ? formatCurrency(distributablePool) : '—'}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">
                      {policyActive
                        ? `dist ${Math.round((distPct || 0) * 100)}%`
                        : 'policy not connected'}
                    </p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-retained">
                    <p className="text-[10px] text-zinc-500">Retained</p>
                    <p className="text-sm font-mono font-medium text-zinc-300">
                      {policyActive ? formatCurrency(retainedAmount) : '—'}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">
                      {policyActive
                        ? `ret ${Math.round((retPct || 0) * 100)}%`
                        : 'policy not connected'}
                    </p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-fines">
                    <p className="text-[10px] text-zinc-500">Fines / Deductions</p>
                    <p className="text-sm font-mono font-medium text-rose-300">
                      {policyActive ? formatCurrency(finesDeductions || 0) : '—'}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">
                      {policyActive ? 'people-rule' : 'policy not connected'}
                    </p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-allocated">
                    <p className="text-[10px] text-zinc-500">Allocated</p>
                    <p className="text-sm font-mono font-medium text-blue-400">
                      {formatCurrency(scCanonical ? scCanonical.allocation?.distributable : scLedger.allocated?.total)}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">
                      {scCanonical
                        ? `by People · r${scCanonical.allocation?.revision}`
                        : `${scLedger.allocated?.count || 0} allocations`}
                    </p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg" data-testid="sc-paid">
                    <p className="text-[10px] text-zinc-500">Paid Out</p>
                    <p className="text-sm font-mono font-medium text-emerald-400">
                      {formatCurrency(scCanonical ? scCanonical.settlement?.paid : scLedger.paid?.total)}
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5" data-testid="sc-paid-basis">
                      {scCanonical ? 'settled by Accounts · by allocation month' : `${scLedger.paid?.count || 0} payouts`}
                    </p>
                  </div>

                  <div className="p-2.5 bg-zinc-800/50 rounded-lg border border-amber-500/20" data-testid="sc-pending">
                    <p className="text-[10px] text-zinc-500">{scCanonical ? 'Outstanding' : 'Pending'}</p>
                    <p className="text-sm font-mono font-semibold text-amber-300">
                      {formatCurrency(scCanonical ? scCanonical.settlement?.outstanding : pendingValue)}
                    </p>
                    <p className="text-[9px] text-amber-500/50 mt-0.5">
                      {scCanonical ? 'to the team · see Service Charge' : ledgerFormula}
                    </p>
                  </div>
                </div>

                {scCanonical ? (
                  <p className="text-[10px] text-zinc-500" data-testid="sc-ledger-date-basis">
                    Allocated / Paid Out / Outstanding are by allocation month ({scCanonical.period}).
                    The P&amp;L “Service Charge Payouts” expense is by payout date and shows{' '}
                    {formatCurrency(pnlData.opex?.service_charge_payouts?.total)} for the selected
                    range, so a payout made in a later month is expensed in that later month.
                    Only paid-out service charge is an expense.
                  </p>
                ) : (
                  <p className="text-[10px] text-zinc-500" data-testid="sc-ledger-date-basis">
                    Paid Out is by payout date for the selected range and reconciles to the P&amp;L
                    “Service Charge Payouts” expense of{' '}
                    {formatCurrency(pnlData.opex?.service_charge_payouts?.total)}. Collected and
                    Pending are the liability position, not an expense.
                  </p>
                )}

                {/* Next payout strip — visible only when scheduled */}
                {policyActive && nextPayout && (
                  <div
                    className="flex items-center justify-between px-3 py-2 rounded-md bg-zinc-800/30 border border-zinc-700/40"
                    data-testid="sc-next-payout"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <CalendarIcon className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-zinc-400">Next payout</span>
                      <span className="font-mono text-zinc-200" data-testid="sc-next-payout-date">
                        {new Date(nextPayout).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    {daysToPayout !== null && daysToPayout !== undefined && (
                      <span
                        className={cn(
                          "text-[11px] font-mono px-2 py-0.5 rounded",
                          daysToPayout < 0 ? 'bg-red-500/10 text-red-400' :
                          daysToPayout <= 3 ? 'bg-amber-500/10 text-amber-300' :
                          'bg-zinc-700/30 text-zinc-300'
                        )}
                        data-testid="sc-days-to-payout"
                      >
                        {daysToPayout < 0
                          ? `${Math.abs(daysToPayout)}d overdue`
                          : daysToPayout === 0
                            ? 'due today'
                            : `${daysToPayout}d to payout`}
                      </span>
                    )}
                  </div>
                )}

                {/* Capture Rate (unchanged) */}
                {scLedger.collected?.capture_rate_pct > 0 && (
                  <div className="flex items-center justify-between text-xs px-1">
                    <span className="text-zinc-500">
                      Capture Rate: <span className="text-zinc-300 font-mono">{scLedger.collected.capture_rate_pct}%</span> of core sales
                    </span>
                    <span className="text-zinc-500">
                      Base: {formatCurrency(scLedger.collected?.core_sales_base)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })()}

          {/* Footer Metadata */}
          <div className="flex items-center justify-between text-xs text-muted-foreground px-2">
            <span>Generated: {pnlData.generated_at ? format(new Date(pnlData.generated_at), 'MMM d, yyyy HH:mm') : '-'}</span>
            <span>Phase {pnlData.phase} | Status: {pnlData.publish_status}</span>
          </div>
          
          {/* Published Snapshots */}
          <PublishedSnapshotsPanel onViewSnapshot={(snapshot) => {
            toast.info(`Viewing: ${snapshot.label}`, {
              description: 'Frozen snapshot - read only'
            });
          }} />
        </>
      )}
    </div>
  );
};

export default PnLPage;
